import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DeepLinkService, linkStamp, parseLinkTime } from '../shared/deep-link.service';
import { uiLocale } from '../shared/chart.utils';

/** One telegram as served by /api/admin/knx/{live,history}; absent fields are omitted by the backend. */
export interface KnxTelegram {
  seq:         number;
  ts:          number;
  gw?:         string;
  service?:    string;
  raw?:        string;
  msg?:        string;
  prio?:       string;
  repeat?:     boolean;
  hops?:       number;
  src?:        string;
  dst?:        string;
  dstType?:    'group' | 'individual';
  tpci?:       string;
  apci?:       string;
  data?:       string;
  dpt?:        string;
  dptGuessed?: boolean;
  value?:      number | boolean | string;
  text?:       string;
  unit?:       string;
  name?:       string;
  group?:      string;
  error?:      string;
  /** Learned name when the GA book has none (inferred, never from ETS). */
  suggestedName?:    string;
  suggestionStatus?: string;
}

/** `truncated`: more than `limit` were newer than `after` and only the newest were returned. */
interface KnxLivePage    { latestSeq: number; truncated?: boolean; telegrams: KnxTelegram[]; }
interface KnxHistoryPage { telegrams: KnxTelegram[]; truncated?: boolean; scannedFiles?: string[]; }

interface KnxGroupStat {
  dst:         string;
  name?:       string;
  group?:      string;
  dpt?:        string;
  dptGuessed?: boolean;
  count:       number;
  firstSeen?:  number;
  lastSeen?:   number;
  lastText?:   string;
  lastValue?:  number | boolean | string;
  lastApci?:   string;
  sources?:    string[];
  suggestedName?:    string;
  suggestionStatus?: string;
}

// ── /api/admin/knx/topology + /suggestions (learned passively from received telegrams) ──

interface KnxTopoGa {
  ga:                string;
  name?:             string;
  suggestedName?:    string;
  suggestionStatus?: string;
  dpt?:              string;
  dptGuessed?:       boolean;
  count?:            number;
  lastText?:         string;
  intervalSec?:      number;
  pwm?:              boolean;
}

/**
 * Device identity from the read-only line scan (device descriptor, serial, manufacturer and order
 * info, read by a separate manual tool) with the backend's small product catalog applied.
 * `confidence` is the catalog match: confirmed | likely | unsure.
 */
interface KnxIdentity {
  ia?:             string;
  line?:           string;
  manufacturer?:   string;
  manufacturerId?: string;
  product?:        string;
  productShort?:   string;      // optional diagram label; derived from `product` when absent
  productType?:    string;
  confidence?:     string;
  sources?:        (string | { url?: string; label?: string; title?: string; text?: string })[];
  basis?:          string;      // what the catalog matched on (German, from the backend)
  mask?:           string;
  maskLabel?:      string;
  serial?:         string;
  hardwareType?:   string;
  orderInfo?:      string;      // hex
  orderText?:      string;
  firmware?:       string;
  firmwareRevision?: string;    // the scan file's spelling, accepted as well
  programVersion?: string;
  scannedAt?:      number | string;
  scanFile?:       string;
}

interface KnxTopoDevice {
  ia:          string;
  line?:       string;
  /** "silent": found only by the line scan, never heard on the bus (actuators without feedback). */
  role?:       string;
  roleLabel?:  string;
  confidence?: number | null;   // 0..1; absent / null for scan-only devices
  reasons?:    string[];
  firstSeen?:  number;
  lastSeen?:   number;
  count?:      number;
  gas?:        KnxTopoGa[];
  identity?:   KnxIdentity | null;
}

interface KnxTopoLine { line: string; gateways?: string[]; router?: string; devices?: string[]; rate?: number; scannedAt?: number | string; }

/** One scanned line's metadata in /api/admin/knx/inventory (field names read tolerantly). */
interface KnxScanMeta {
  line?:           string;
  router?:         string;
  scanFile?:       string;
  file?:           string;
  scannedAt?:      number | string;
  probed?:         unknown;     // ["1.1.0", "1.1.239"] or { from, to }
  requested?:      unknown;
  aborted?:        boolean;
  abortReason?:    string | null;
  conOnly?:        unknown;     // IAs that accepted a connection but never answered
  conOnlyNoReply?: unknown;
  reconnects?:     number;
}

/** GET /api/admin/knx/inventory: identities of all scanned devices plus the scan metadata per line. */
interface KnxInventory {
  dirs?:     unknown[];         // [{ path, exists, files }] or plain paths
  devices?:  unknown[];
  lines?:    unknown;
  scans?:    unknown;
  files?:    unknown;
  warnings?: unknown;           // ["<file>: unreadable (…)", "<file>: devices[3] … — skipped", …]
}
interface KnxIpNode   { ip: string; label?: string; kind?: string; }

interface KnxTopology {
  area?:         number | string;
  areas?:        (number | string)[];
  lines?:        KnxTopoLine[];
  devices?:      KnxTopoDevice[];
  ipNodes?:      KnxIpNode[];
  learnedSince?: number;
  telegrams?:    number;
  replay?:       { done?: boolean; files?: number | string[]; telegrams?: number };
}

interface KnxSuggestion {
  ga:             string;
  suggestedName?: string;
  name?:          string;
  status?:        string;          // accepted | candidate | hint
  confidence?:    number;          // 0..1
  evidence?:      string | string[];
  matches?:       number;
  total?:         number;
  dpt?:           string;
  dptGuessed?:    boolean;
  senders?:       string[];
  target?:        { kind?: string; deviceId?: string; name?: string; matches?: number; total?: number };
  candidates?:    { name?: string; deviceId?: string; matches?: number; total?: number; score?: number }[];
}

interface KnxStatus {
  listening:      boolean;
  iface?:         string;
  localIp?:       string;
  group?:         string;
  port?:          number;
  subnet?:        string;
  startedAt?:     number;
  lastTelegramAt?: number;
  total?:         number;
  ratePerMin?:    number;
  perGateway?:    Record<string, number>;
  perApci?:       Record<string, number>;
  lost?:          number;
  busy?:          number;
  dropped?:       number;
  lastError?:     string;
  lastErrorAt?:   number;
  logDir?:        string;
  retentionDays?: number;
  files?:         { date: string; bytes: number }[];
  gaBook?:        { entries?: number; loadedAt?: number; file?: string; warnings?: string[] };
}

interface KnxGaUploadResult { entries?: number; mains?: number; middles?: number; warnings?: string[]; }

/** Telegram prepared for the table once on arrival (no formatting work in the template). */
interface TelegramRow {
  uid:        number;          // client-side identity: seq restarts with the backend and repeats across history days
  t:          KnxTelegram;
  time:       string;          // HH:MM:SS.mmm
  day:        string;          // DD.MM. (Verlauf only)
  line:       string;          // area.line of the source ("1.3")
  isGroup:    boolean;
  apciLabel:  string;          // "Write" for GroupValueWrite, else the full name / service
  apciCls:    string;
  data:       string;          // spaced hex
  dptLabel:   string;          // "~9" when guessed
  sug:        SugStatus | '';  // a learned name is shown (no book name): its status
  hay:        string;          // lower-case text the free-text filter searches
}

interface GroupRow {
  g:         KnxGroupStat;
  key:       number;           // numeric GA for sorting
  dptLabel:  string;
  lastText:  string;
  lastFull:  string;
  ago:       string;
  sources:   string;
  sug:       SugStatus | '';
  hay:       string;
}

type SugStatus = 'accepted' | 'candidate' | 'hint';

/** Role groups the frontend colours and translates; the backend's role key is mapped onto one of them. */
type RoleKey = 'time' | 'heating' | 'roomTemp' | 'weather' | 'switch' | 'blind' | 'dimmer' | 'presence' | 'meter' | 'unknown' | 'silent';
const ROLE_ORDER: RoleKey[] = ['time', 'heating', 'roomTemp', 'weather', 'switch', 'blind', 'dimmer', 'presence', 'meter', 'unknown', 'silent'];

type IdentConf = 'confirmed' | 'likely' | 'unsure';

/** `label`: the source's own wording ('' for a bare URL); `text`: what the link shows. */
interface IdentSource { text: string; label: string; url: string | null; title: string; }

/** Identity prepared once per poll for the list, the diagram, the inventory and the CSV. */
interface IdentView {
  i:         KnxIdentity;
  mfr:       string;          // "MDT technologies", else "Hersteller 0x00C5"
  mfrShort:  string;          // "MDT"
  mfrId:     string;          // "0x0083"
  known:     boolean;         // a catalog product, not "unbekanntes Produkt"
  product:   string;          // full catalog name (CSV, tooltips)
  bare:      string;          // the product without its leading manufacturer word (shown next to the manufacturer)
  short:     string;          // diagram label "MDT AKH-0600"
  type:      string;
  conf:      IdentConf;
  confLabel: string;
  sources:   IdentSource[];
  basis:     string;
  scanned:   string;          // formatted scan date
  rows:      { k: string; v: string; mono: boolean }[];   // the Details expander
  hay:       string;
}

/** Heard on the bus, or found only by the scan ('pending' until the topology answered). */
type InvBus = 'heard' | 'silent' | 'pending';

interface InvRow {
  ia:        string;
  key:       number;
  line:      string;
  ident:     IdentView | null;
  /** Without an identity: "not found by the line scan" / "line not scanned". */
  noIdent:   string;
  bus:       InvBus;
  busLabel:  string;
  busTitle:  string;
  telegrams: number;
  hay:       string;
}

interface InvLine {
  line:     string;
  key:      number;
  title:    string;
  meta:     string;            // "IP-Router 10.0.1.10 · Scan 13.10.26, 21:40 · geprüft 1.1.0–1.1.239 · line-1.1.json"
  file:     string;            // full path of the scan file (tooltip)
  warn:     string;            // scan aborted (reason)
  conOnly:  string;            // connected without a reply
  rows:     InvRow[];
}

/** Common TP1 mask versions (device descriptor type 0): the profile name, or an i18n key (ADMIN_…) for the
 *  device kind; the raw value is always shown too. */
const MASK_NAMES: Record<string, string> = {
  '0010': 'System 1', '0011': 'System 1', '0012': 'System 1',
  '0020': 'System 2', '0700': 'System 7', '0701': 'System 7', '0705': 'System 7', '07B0': 'System B',
  '0910': 'ADMIN_KNX_MASK_COUPLER', '0911': 'ADMIN_KNX_MASK_COUPLER', '0912': 'ADMIN_KNX_MASK_COUPLER',
  '091A': 'ADMIN_KNX_MASK_IP_ROUTER',
};
/** Words a shortened diagram label must not end on. */
const SHORT_STOP = new Set(['mit', 'oder', 'und', 'für', 'ältere', 'with', 'or', 'and', 'for', 'older']);

interface TopoGaRow {
  g:         KnxTopoGa;
  key:       number;
  name:      string;           // book name, else the learned one
  sug:       SugStatus | '';
  dptLabel:  string;
  interval:  string;
  count:     string;
}

interface TopoDevice {
  d:          KnxTopoDevice;
  ia:         string;
  key:        number;
  line:       string;
  role:       RoleKey;
  roleLabel:  string;          // German UI: the backend's specific label; else the translated group
  roleShort:  string;          // translated group (diagram box)
  roleTitle:  string;
  conf:       number | null;   // 0..100
  reasons:    string[];
  count:      string;          // "12 Telegramme"
  ago:        string;
  lastFull:   string;
  firstFull:  string;
  gas:        TopoGaRow[];
  /** Product from the line scan; null when the device is not in a scan result. */
  ident:      IdentView | null;
  /** Found only by the line scan, never heard on the bus. */
  silent:     boolean;
  /** Heard on a line that was scanned, inside the probed range, but the scan result does not list it. */
  notInScan:  boolean;
  /** Heard on a scanned line outside the probed range (e.g. a tunnelling address x.y.240+): "not probed (probed …)"; '' otherwise. */
  notProbed:  string;
  hay:        string;
}

interface TopoLine {
  line:      string;
  key:       number;
  backbone:  boolean;          // x.0: the IP backbone itself (no line coupler)
  title:     string;           // "Linie 1.1" / "Hauptlinie 1.0"
  gateways:  string[];
  gwText:    string;           // "IP-Router 10.0.1.10 · 1.1.0" / "über 10.0.1.13 (Gira HomeServer 4)"
  gwTitle:   string;
  router:    string;
  rate:      string;
  devCount:  string;
  /** "6 nur per Scan" when some devices were never heard; '' otherwise. */
  silentText: string;
  /** Date of the line scan result ('' when the line was not scanned). */
  scanned:   string;
  scannedAt: number | null;
  devices:   TopoDevice[];
}

interface SvgBox     { x: number; y: number; w: number; h: number; }
interface SvgIpNode  extends SvgBox { title: string; ip: string; sub: string; listener: boolean; tip: string; }
interface SvgDev     extends SvgBox { v: TopoDevice; prod: string; role: string; roleY: number; conf: string; tip: string; }
interface SvgCol     extends SvgBox { l: TopoLine; l1: string; l2: string; l3: string; tip: string; devs: SvgDev[]; }
/** `ids`: some boxes carry a product line from the scan (taller boxes, legend note). */
interface TopoSvg    { w: number; h: number; nodes: SvgIpNode[]; band: SvgBox & { label: string }; cols: SvgCol[]; ipPath: string; tpPath: string; ids: boolean; }

interface SuggestionRow {
  s:          KnxSuggestion;
  key:        number;
  name:       string;          // the ETS book name if there is one, else the learned name
  suggested:  string;          // the learned name
  bookName:   string;          // '' when the GA book has no name for it (an open suggestion)
  differs:    boolean;         // book name and learned name differ
  status:     SugStatus;
  conf:       number | null;
  hits:       string;          // "12/12 Treffer"
  evidence:   string;
  candidates: string;
  dptLabel:   string;
  senders:    string;
  hay:        string;
}

interface CountRow { key: string; count: number; pct: number; }
interface FileRow  { date: string; size: string; }

interface StatusView {
  s:          KnxStatus;
  started:    string;
  gateways:   CountRow[];
  apcis:      CountRow[];
  files:      FileRow[];
  gaLoaded:   string;
  gaWarnings: string[];
  lastTelegramAgo:  string;
  lastTelegramFull: string;
  lastErrorAt:      string;
}

export type AdminKnxTab = 'live' | 'topology' | 'valves' | 'groups' | 'history' | 'status';
type GroupSort = 'ga' | 'count' | 'last';

const TAB_KEY = 'adminKnxTab';
/** Rows kept in the live table (oldest dropped). */
const MAX_ROWS = 2000;
/** Normal page size and the catch-up page size (first load, resume after a pause). */
const LIVE_LIMIT = 500;
const CATCH_UP   = 1000;
const LIVE_MS    = 2000;
const STATUS_MS  = 10000;
const GROUPS_MS  = 15000;
const TOPO_MS    = 30000;
/** The scan inventory changes rarely (the backend re-reads its files at most once a minute). */
const INV_MS     = 60000;
const INV_OPEN_KEY = 'adminKnxInvOpen';
/** Scan-file warnings shown at most (the rest is counted). */
const INV_WARN_MAX = 10;
/** KNXnet/IP routing defaults, shown in the diagram until the status answered. */
const KNX_MCAST  = '224.0.23.12';
const KNX_PORT   = 3671;
/** Same cap as the backend's request body limit for the CSV upload. */
const GA_MAX_BYTES = 8 * 1024 * 1024;
/** Offered in the APCI selects before (or besides) what the bus actually showed. */
const BASE_APCIS = ['GroupValueWrite', 'GroupValueResponse', 'GroupValueRead'];
/** An APCI name from a link (shown in a select, sent as a query parameter). */
const APCI_RE = /^[A-Za-z][A-Za-z0-9_]{0,47}$/;
const IA_RE = /^\d{1,2}\.\d{1,2}\.\d{1,3}$/;
const HIST_LIMIT = 1000;
/** Verlauf quick ranges, relative to now: a link to "the last 24 h" stays relative. */
const HIST_RELS: Record<string, number> = { '1h': 3600_000, '24h': 86_400_000, '7d': 7 * 86_400_000 };
const SUG_FILTERS = ['accepted', 'candidate', 'hint', 'book'] as const;

/**
 * Deep-link keys of this section ("admin.knx…": they survive tab and view changes; the dashboard owns
 * "admin" itself). The sub-tab is always written (the stored default differs per browser); the Verlauf
 * keys are written when a search runs, so a link shows the search its results came from.
 */
const LINK = {
  tab:        'admin.knx',
  liveQ:      'admin.knx.live.q',
  liveApci:   'admin.knx.live.apci',
  liveGroups: 'admin.knx.live.groups',
  topoQ:      'admin.knx.topo.q',
  topoDev:    'admin.knx.topo.dev',
  topoList:   'admin.knx.topo.list',
  topoSug:    'admin.knx.topo.sug',
  groupQ:     'admin.knx.groups.q',
  groupSort:  'admin.knx.groups.sort',
  histLast:   'admin.knx.hist.last',
  histFrom:   'admin.knx.hist.from',
  histTo:     'admin.knx.hist.to',
  histQ:      'admin.knx.hist.q',
  histGa:     'admin.knx.hist.ga',
  histSrc:    'admin.knx.hist.src',
  histApci:   'admin.knx.hist.apci',
  histLimit:  'admin.knx.hist.limit',
  histOrder:  'admin.knx.hist.order',
} as const;
/** The keys of a Verlauf search (any of them in a link: the search runs when Verlauf is shown). */
const HIST_SEARCH_KEYS = [LINK.histLast, LINK.histFrom, LINK.histTo, LINK.histQ, LINK.histGa, LINK.histSrc, LINK.histApci, LINK.histLimit];

const pad2 = (n: number) => String(n).padStart(2, '0');

/** "06 40" from "0640". */
function spacedHex(hex: string | undefined): string {
  return hex ? (hex.match(/.{1,2}/g) ?? []).join(' ') : '';
}

/** Sort key of a 3-level group ("16/4/1") or individual address ("1.3.16"). */
function addrKey(a: string): number {
  const p = a.split(/[/.]/).map(n => parseInt(n, 10) || 0);
  return (p[0] ?? 0) * 65536 + (p[1] ?? 0) * 256 + (p[2] ?? 0);
}

/** Badge text: "Write" for GroupValueWrite; without an APCI the routing service ("LOST", "BUSY"),
 *  "⚠" for a frame that did not parse, or the TPCI of a transport control frame (UCD / NCD). */
function apciLabel(t: KnxTelegram): string {
  const a = t.apci;
  if (a) return a.startsWith('GroupValue') ? a.slice(10) : a;
  if (t.service && t.service !== 'ROUTING_INDICATION') return t.service.replace(/^ROUTING_/, '').replace('_MESSAGE', '');
  return t.error ? '⚠' : t.tpci || '?';
}

function apciClass(t: KnxTelegram): string {
  const a = t.apci;
  if (!a) return t.error || (t.service && t.service !== 'ROUTING_INDICATION') ? 'knxm-apci-svc' : 'knxm-apci-other';
  if (a.endsWith('Write'))    return a === 'GroupValueWrite' ? 'text-bg-primary' : 'knxm-apci-other';
  if (a.endsWith('Response')) return a === 'GroupValueResponse' ? 'text-bg-success' : 'knxm-apci-other';
  if (a.endsWith('Read'))     return a === 'GroupValueRead' ? 'text-bg-warning' : 'knxm-apci-other';
  return 'knxm-apci-other';
}

/** Maps whatever spelling the backend uses ("heatingActuator", "ROOM_TEMP_SENSOR", "pushButtonTemp" …) onto a role group. */
function roleKey(role: string | undefined): RoleKey {
  const r = (role ?? '').toLowerCase().replace(/[^a-z]/g, '');
  if (/^(silent|scanonly|scan)$/.test(r))                 return 'silent';
  if (/time|clock|zeit/.test(r))                          return 'time';
  if (/heat|valve|heiz/.test(r))                          return 'heating';
  if (/weather|wetter/.test(r))                           return 'weather';
  if (/presence|motion|praesenz|bewegung/.test(r))        return 'presence';
  if (/blind|shutter|jalou|rollad|rollla|rollo/.test(r))  return 'blind';
  if (/dimm/.test(r))                                     return 'dimmer';
  if (/temp|thermo|button|taster|room/.test(r))           return 'roomTemp';
  if (/meter|counter|zaehler|energy/.test(r))             return 'meter';
  if (/switch|schalt/.test(r))                            return 'switch';
  return 'unknown';
}

/** Missing or unknown status counts as "candidate": never claim more certainty than the backend did. */
function sugStatus(s: string | undefined): SugStatus {
  const v = (s ?? '').toLowerCase();
  if (v === 'accepted' || v === 'sicher') return 'accepted';
  if (v === 'hint' || v === 'hinweis')    return 'hint';
  return 'candidate';
}

/** 0..1 (or already 0..100) → whole percent; null when absent. */
function percent(c: number | undefined): number | null {
  if (typeof c !== 'number' || !isFinite(c)) return null;
  return Math.max(0, Math.min(100, Math.round(c > 1 ? c : c * 100)));
}

/** SVG text has no ellipsis: cut to a character budget. */
function clip(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : typeof v === 'number' && isFinite(v) ? String(v) : '';
}

/** Catalog match → badge. Missing or unknown counts as "unsure": never claim more certainty than the backend did. */
function identConf(c: unknown): IdentConf {
  const v = str(c).toLowerCase();
  if (v === 'confirmed' || v === 'bestätigt')    return 'confirmed';
  if (v === 'likely' || v === 'wahrscheinlich') return 'likely';
  return 'unsure';
}

/** Epoch ms from ms, seconds or an ISO string; null when absent. */
function toMs(v: unknown): number | null {
  const s = str(v);
  const n = typeof v === 'number' ? v : s ? (/^\d+$/.test(s) ? Number(s) : Date.parse(s)) : NaN;
  if (!isFinite(n) || n <= 0) return null;
  return n < 1e11 ? n * 1000 : n;
}

/** "0x0083" from "0083" / "83" / "0x83". */
function hexId(id: unknown): string {
  const h = str(id).replace(/^0x/i, '');
  return /^[0-9a-f]{1,4}$/i.test(h) ? '0x' + h.toUpperCase().padStart(4, '0') : str(id);
}

/** KNX serial number as ETS shows it: "0083:8B400804". */
function fmtSerial(s: unknown): string {
  const h = str(s);
  return /^[0-9a-f]{12}$/i.test(h) ? `${h.slice(0, 4)}:${h.slice(4)}`.toUpperCase() : h;
}

/** Order text only when it is readable ASCII (unprogrammed or old devices report 0xFF bytes). */
function printable(s: unknown): string {
  const t = str(s).replace(/\0+$/, '').trim();
  return /^[\x20-\x7e]+$/.test(t) ? t : '';
}

/** Sources as text + optional link: strings may carry a URL, objects are { url, label }. Only http(s) becomes a link. */
function identSources(list: unknown): IdentSource[] {
  const out: IdentSource[] = [];
  for (const s of Array.isArray(list) ? list : list ? [list] : []) {
    let text = '';
    let url: string | null = null;
    if (typeof s === 'string') {
      const m = s.match(/https?:\/\/[^\s<>"']+/i);
      url = m ? m[0].replace(/[.,;)]+$/, '') : null;
      text = (url ? s.replace(m![0], ' ') : s).replace(/\s+/g, ' ').replace(/^[\s,;:–-]+|[\s,;:–(-]+$/g, '').trim();
    } else if (s && typeof s === 'object') {
      const o = s as Record<string, unknown>;
      const u = str(o['url']);
      url = /^https?:\/\//i.test(u) ? u : null;
      text = str(o['label']) || str(o['title']) || str(o['text']);
    }
    if (!text && !url) continue;
    out.push({ text: text || clip(url!.replace(/^https?:\/\/(www\.)?/i, ''), 64), label: text, url, title: url ?? text });
  }
  return out;
}

/**
 * Diagram label: manufacturer + model code without the revision ("MDT AKH-0600"), else the first words of
 * the product within ~24 characters ("Gira Sensor?"); "?" marks an unsure match.
 */
function productShort(product: string, mfrShort: string, unsure: boolean): string {
  const p = product.replace(/\s*\(.*$/, '').trim();
  const code = p.match(/\b[A-Z]{2,}[A-Z0-9]*-[A-Z0-9]+(?:\.\d{2})?\b/);
  let s: string;
  if (code) {
    s = `${mfrShort} ${code[0].replace(/\.\d{2}$/, '')}`.trim();
  } else {
    const words = p.split(/\s+/).filter(w => w && !/^(knx|secure)$/i.test(w));
    if (mfrShort && words[0]?.toLowerCase() !== mfrShort.toLowerCase()) words.unshift(mfrShort);
    const kept: string[] = [];
    for (const w of words) {
      if (kept.length && [...kept, w].join(' ').length > 24) break;
      kept.push(w);
    }
    while (kept.length > 1 && SHORT_STOP.has(kept[kept.length - 1].toLowerCase())) kept.pop();
    s = clip(kept.join(' '), 24);
  }
  return unsure && !s.endsWith('?') ? s + '?' : s;
}

/** "1.1.0–1.1.239" from ["1.1.0", "1.1.239"], { from, to } or a plain string. */
function rangeText(r: unknown): string {
  if (Array.isArray(r)) return r.length ? (r.length === 2 ? `${str(r[0])}–${str(r[1])}` : r.map(str).join(', ')) : '';
  if (r && typeof r === 'object') {
    const o = r as Record<string, unknown>;
    const a = str(o['from'] ?? o['first'] ?? o['start']);
    const b = str(o['to'] ?? o['last'] ?? o['end']);
    return a && b ? `${a}–${b}` : a || b;
  }
  return str(r);
}

/** Device numbers [first, last] of a scan range (["1.1.0", "1.1.239"] or { from, to }); null when unknown. */
function rangeBounds(r: unknown): [number, number] | null {
  let a: unknown, b: unknown;
  if (Array.isArray(r)) {
    if (r.length !== 2) return null;
    [a, b] = r;
  } else if (r && typeof r === 'object') {
    const o = r as Record<string, unknown>;
    a = o['from'] ?? o['first'] ?? o['start'];
    b = o['to'] ?? o['last'] ?? o['end'];
  } else {
    return null;
  }
  const dev = (x: unknown) => {
    const m = /^\d{1,2}\.\d{1,2}\.(\d{1,3})$/.exec(str(x));
    return m ? Number(m[1]) : NaN;
  };
  const lo = dev(a), hi = dev(b);
  return lo <= hi ? [lo, hi] : null;    // NaN compares false
}

/** IAs from strings or { ia } objects. */
function iaList(v: unknown): string[] {
  return (Array.isArray(v) ? v : []).map(x => typeof x === 'string' ? x : str((x as Record<string, unknown> | null)?.['ia']))
    .filter(Boolean);
}

/** One CSV field (";"-separated, always quoted); a leading = + - @ is defused for spreadsheets. */
function csvField(v: unknown): string {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  return `"${s.replace(/"/g, '""')}"`;
}

/** Value of a datetime-local input (local time, minutes). */
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * Admin → KNX: passive bus monitor (live table, learned devices & topology, group address statistics,
 * log search, listener status). Strictly read-only towards KNX — the only write is the GA CSV upload to
 * our own backend. Everything under "Geräte & Topologie" is inferred by the backend from received
 * telegrams and HomeServer data it already holds; this component only shows it.
 * Pollers run only for the visible inner tab while the browser tab is visible; the component only
 * exists while the admin overlay is open on this section, so closing it stops everything (ngOnDestroy).
 */
@Component({
  selector: 'app-admin-knx',
  templateUrl: './admin-knx.component.html',
  // The dashboard ticks every second; OnPush keeps the 2000-row table out of those cycles.
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; overflow: hidden; }
    .knxm-pane { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
    .knxm-scroll { flex: 1 1 auto; min-height: 0; overflow: auto; -webkit-overflow-scrolling: touch; }
    .knxm-tabs { display: flex; background: var(--bs-tertiary-bg); border-radius: 10px; padding: 3px; gap: 3px;
                 overflow-x: auto; scrollbar-width: none; }
    .knxm-tabs > .nav-tab-btn { flex: 1 1 0; min-width: 0; line-height: 1.15; }
    /* Below 992 px: icon above the short label; from 992 px icon and full label in one line */
    .knxm-tab-icon  { display: block; font-size: 1rem; }
    .knxm-tab-label { display: block; min-width: 0; font-size: .66rem; overflow: hidden; text-overflow: ellipsis; }
    .knxm-bar { display: flex; flex-wrap: wrap; align-items: center; gap: .4rem .5rem; }
    .knxm-bar .form-control, .knxm-bar .form-select { font-size: .75rem; }
    .knxm-q { flex: 1 1 11rem; min-width: 8rem; max-width: 22rem; }
    .knxm-sel { width: auto; max-width: 13rem; }
    .knxm-check { font-size: .75rem; white-space: nowrap; }
    .knxm-checks { display: flex; align-items: center; gap: .4rem .5rem; }
    .knxm-stats { font-size: .7rem; color: var(--bs-secondary-color); font-variant-numeric: tabular-nums; white-space: nowrap; }
    .knxm-dot { display: inline-block; width: .5rem; height: .5rem; border-radius: 50%; margin-right: .25rem; }
    .knxm-table { font-size: .72rem; min-width: 760px; }
    .knxm-table th { position: sticky; top: 0; z-index: 1; background: var(--bs-body-bg); font-weight: 600;
                     white-space: nowrap; color: var(--bs-secondary-color); box-shadow: inset 0 -1px 0 var(--bs-border-color); }
    .knxm-table td { vertical-align: top; padding-top: .2rem; padding-bottom: .2rem; }
    .knxm-table tbody tr { cursor: pointer; }
    .knxm-sortable { cursor: pointer; user-select: none; }
    .mono { font-family: var(--bs-font-monospace); }
    .nowrap { white-space: nowrap; }
    .knxm-sub { font-size: .64rem; color: var(--bs-secondary-color); line-height: 1.2; }
    .knxm-name { max-width: 16rem; line-height: 1.2; }
    .knxm-data { max-width: 9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .knxm-val { min-width: 9rem; }
    .knxm-apci { font-size: .64rem; font-weight: 600; }
    .knxm-apci-other { background: var(--bs-secondary-bg); color: var(--bs-body-color); border: 1px solid var(--bs-border-color); }
    .knxm-apci-svc { background: var(--bs-danger-bg-subtle); color: var(--bs-danger-text-emphasis); }
    .knxm-dpt { font-size: .6rem; font-weight: 600; margin-right: .3rem; vertical-align: .05rem; }
    .knxm-dpt-book  { background: var(--bs-info-bg-subtle); color: var(--bs-info-text-emphasis); }
    .knxm-dpt-guess { background: transparent; color: var(--bs-secondary-color); border: 1px dashed var(--bs-border-color); }
    .knxm-err td { background: var(--bs-danger-bg-subtle); }
    .knxm-detail td { background: var(--bs-tertiary-bg); font-size: .68rem; cursor: default; }
    .knxm-detail .mono { word-break: break-all; }
    .knxm-kv { display: grid; grid-template-columns: max-content 1fr; gap: .1rem .6rem; }
    .knxm-card-title { font-size: .7rem; letter-spacing: .05em; text-transform: uppercase; color: var(--bs-secondary-color); font-weight: 600; }
    .knxm-num { font-variant-numeric: tabular-nums; text-align: right; white-space: nowrap; }
    .knxm-bar-bg { height: 4px; border-radius: 2px; background: var(--bs-secondary-bg); overflow: hidden; min-width: 3rem; }
    .knxm-bar-bg > span { display: block; height: 100%; background: var(--bs-primary); }
    .knxm-small { font-size: .78rem; }
    .knxm-hint { font-size: .68rem; color: var(--bs-secondary-color); }
    /* Learned (not ETS) names: muted, with a small dashed badge */
    .knxm-sugg { color: var(--bs-secondary-color); font-style: italic; }
    .knxm-sugg-badge { font-size: .56rem; font-weight: 600; font-style: normal; vertical-align: .08rem; margin-left: .3rem;
                       color: var(--bs-secondary-color); border: 1px dashed var(--bs-border-color); background: transparent; }

    @media (min-width: 576px) { .knxm-tab-label { font-size: .75rem; } }
    @media (min-width: 992px) {
      .knxm-tabs > .nav-tab-btn { display: flex; align-items: center; justify-content: center; line-height: 1.5; }
      .knxm-tab-icon { display: inline; font-size: inherit; margin-right: .3em; }
      .knxm-tab-label { font-size: inherit; }
    }
    /* Phones: the Verlauf form scrolls away with the results instead of squeezing them */
    @media (max-width: 767.98px) {
      .knxm-hist { overflow-y: auto; }
      .knxm-hist > .knxm-scroll { flex: none; overflow: auto hidden; }
    }
  `, `
    /* ── Geräte & Topologie (own sheet: keeps each under the component style budget) ── */
    /* Role colours: one hue per role group, lighter tints on dark backgrounds */
    :host { --r1: #6610f2; --r2: #dc3545; --r3: #e8690b; --r4: #0a97b4; --r5: #b58903;
            --r6: #1aa179; --r7: #a0522d; --r8: #d63384; --r9: #198754; --r0: #6c757d; }
    :host-context([data-bs-theme="dark"]) { --r1: #a370f7; --r2: #ea868f; --r3: #feb272; --r4: #6edff6; --r5: #ffda6a;
                                            --r6: #79dfc1; --r7: #d9a27c; --r8: #e685b5; --r9: #75b798; --r0: #adb5bd; }
    .knxr-time { --rc: var(--r1); }  .knxr-heating { --rc: var(--r2); }  .knxr-roomTemp { --rc: var(--r3); }
    .knxr-weather { --rc: var(--r4); }  .knxr-switch { --rc: var(--r5); }  .knxr-blind { --rc: var(--r6); }
    .knxr-dimmer { --rc: var(--r7); }  .knxr-presence { --rc: var(--r8); }  .knxr-meter { --rc: var(--r9); }
    .knxr-unknown { --rc: var(--r0); }
    .knxt-dot { display: inline-block; flex: none; width: .6rem; height: .6rem; border-radius: 3px; background: var(--rc); }
    .knxt-legend { display: flex; flex-wrap: wrap; gap: .25rem .8rem; font-size: .68rem; color: var(--bs-secondary-color); }
    .knxt-legend > span { display: inline-flex; align-items: center; gap: .3rem; white-space: nowrap; }
    /* The diagram keeps its natural size and scrolls sideways on phones (text would be unreadable scaled down) */
    .knxt-svg-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .knxt-svg { display: block; margin: 0 auto; font-family: var(--bs-body-font-family); }
    .knxt-svg text { fill: var(--bs-body-color); font-size: 11px; }
    .knxt-svg .t-b    { font-weight: 600; }
    .knxt-svg .t-sub  { fill: var(--bs-secondary-color); font-size: 10px; }
    .knxt-svg .t-mono { font-family: var(--bs-font-monospace); }
    .knxt-svg .t-end  { text-anchor: end; }
    .knxt-node rect { fill: var(--bs-body-bg); stroke: var(--bs-border-color); }
    .knxt-node.listener rect { stroke: var(--bs-primary); stroke-dasharray: 4 3; }
    .knxt-band rect { fill: var(--bs-primary-bg-subtle); stroke: var(--bs-primary-border-subtle); }
    .knxt-band text { fill: var(--bs-primary-text-emphasis); font-size: 10.5px; font-weight: 600; }
    .knxt-head rect { fill: var(--bs-tertiary-bg); stroke: var(--bs-border-color); }
    .knxt-head.backbone rect { stroke: var(--bs-primary-border-subtle); }
    .knxt-ip { fill: none; stroke: var(--bs-primary); stroke-width: 2; }
    .knxt-tp { fill: none; stroke: var(--bs-success); stroke-width: 2.5; }
    .knxt-d { cursor: pointer; }
    .knxt-d .bg    { fill: var(--bs-body-bg); }
    .knxt-d .tint  { fill: var(--rc); fill-opacity: .14; stroke: var(--rc); stroke-width: 1.2; }
    .knxt-d .bar   { fill: var(--rc); }
    .knxt-d:hover .tint { fill-opacity: .24; }
    .knxt-d.sel .tint   { stroke: var(--bs-emphasis-color); stroke-width: 2.5; }
    .knxt-d:focus { outline: none; }
    .knxt-d:focus-visible .tint { stroke: var(--bs-primary); stroke-width: 3; }
    .knxt-d.dim { opacity: .22; }
    /* Device list */
    .knxt-q { flex: 1 1 11rem; min-width: 8rem; max-width: 22rem; font-size: .75rem; }
    /* No overflow clipping: the scan sources card may leave the box; the head and the last row round themselves */
    .knxt-line { border: 1px solid var(--bs-border-color); border-radius: .5rem; }
    .knxt-line > :first-child { border-radius: calc(.5rem - 1px) calc(.5rem - 1px) 0 0; }
    .knxt-line > :last-child { border-bottom-left-radius: calc(.5rem - 1px); border-bottom-right-radius: calc(.5rem - 1px); }
    .knxt-line + .knxt-line { margin-top: .5rem; }
    .knxt-line-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: .1rem .5rem; width: 100%; text-align: left;
                      border: 0; background: var(--bs-tertiary-bg); color: var(--bs-body-color); padding: .35rem .6rem; font-size: .78rem; }
    .knxt-chev { display: inline-block; width: .8rem; color: var(--bs-secondary-color); }
    .knxt-dev { padding: .45rem .6rem; border-top: 1px solid var(--bs-border-color-translucent); font-size: .74rem; min-width: 0; }
    .knxt-dev.sel { background: var(--bs-primary-bg-subtle); }
    .knxt-dev-head { display: flex; flex-wrap: wrap; align-items: center; gap: .15rem .5rem; }
    .knxt-dev-head.pick { cursor: pointer; border-radius: .25rem; }
    .knxt-dev-head.pick:focus { outline: none; }
    .knxt-dev-head.pick:focus-visible { outline: 2px solid var(--bs-primary); outline-offset: 2px; }
    .knxt-role { font-weight: 600; min-width: 0; overflow-wrap: anywhere; }
    .knxt-conf { display: inline-flex; align-items: center; gap: .3rem; font-size: .66rem; color: var(--bs-secondary-color);
                 font-variant-numeric: tabular-nums; white-space: nowrap; }
    .knxt-conf .knxm-bar-bg { width: 3rem; min-width: 3rem; }
    .knxt-conf .knxm-bar-bg > span { background: var(--rc); }
    .knxt-reasons { margin: .2rem 0 0; padding-left: 1.1rem; font-size: .68rem; color: var(--bs-secondary-color); overflow-wrap: anywhere; }
    .knxt-tbl-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
    .knxt-tbl { font-size: .7rem; margin: .3rem 0 0; }
    .knxt-tbl th { font-weight: 600; white-space: nowrap; color: var(--bs-secondary-color); background: transparent; }
    .knxt-tbl td, .knxt-tbl th { padding: .15rem .4rem; vertical-align: top; background: transparent; }
    .knxt-gas { min-width: 580px; table-layout: fixed; }
    .knxt-gas .w-ga { width: 4.4rem; } .knxt-gas .w-dpt { width: 4.4rem; } .knxt-gas .w-val { width: 6.4rem; }
    .knxt-gas .w-iv { width: 5.6rem; } .knxt-gas .w-n { width: 3.4rem; }
    .knxt-gas td { overflow-wrap: anywhere; }
    .knxt-sug { min-width: 680px; font-size: .72rem; }
    .knxt-pwm { font-size: .56rem; font-weight: 600; background: var(--bs-warning-bg-subtle); color: var(--bs-warning-text-emphasis);
                vertical-align: .08rem; margin-left: .3rem; }
    .knxt-st { font-size: .62rem; font-weight: 600; }
    .knxt-st-accepted  { background: var(--bs-success-bg-subtle); color: var(--bs-success-text-emphasis); }
    .knxt-st-candidate { background: var(--bs-warning-bg-subtle); color: var(--bs-warning-text-emphasis); }
    .knxt-st-hint      { background: var(--bs-secondary-bg); color: var(--bs-secondary-color); border: 1px dashed var(--bs-border-color); }
    /* Suggestions for GAs the ETS book already names */
    .knxt-book    { font-size: .56rem; font-weight: 600; vertical-align: .08rem; margin-left: .3rem;
                    background: var(--bs-info-bg-subtle); color: var(--bs-info-text-emphasis); }
    .knxt-differs { font-size: .56rem; font-weight: 600; vertical-align: .08rem; margin-right: .3rem;
                    background: var(--bs-warning-bg-subtle); color: var(--bs-warning-text-emphasis); }
    .knxt-sug-book > td { opacity: .78; }
    .knxt-sug-conf .knxm-bar-bg { width: 3.2rem; min-width: 3.2rem; }
    .knxt-c-accepted .knxm-bar-bg > span  { background: var(--bs-success); }
    .knxt-c-candidate .knxm-bar-bg > span { background: var(--bs-warning); }
    .knxt-c-hint .knxm-bar-bg > span      { background: var(--bs-secondary-color); }
    .knxt-close { line-height: 1; padding: .1rem .45rem; }
  `, `
    /* ── Line scan: product identity, scan-only devices, inventory (own sheet: style budget) ── */
    .knxr-silent { --rc: var(--r0); }
    .knxt-dot-silent { background: transparent; border: 1.5px dashed var(--rc); }
    .knxt-d.silent .bg   { fill-opacity: .6; }
    .knxt-d.silent .tint { fill-opacity: .03; stroke-dasharray: 4 3; }
    .knxt-d.silent .bar  { opacity: .3; }
    .knxt-d.silent:hover .tint { fill-opacity: .1; }
    .knxt-svg .t-prod { font-size: 10px; font-weight: 600; }
    .knxt-d.silent text { fill: var(--bs-secondary-color); }
    /* Identity line of a device (list + card) */
    .knxi-row { position: relative; display: flex; flex-wrap: wrap; align-items: baseline; gap: .1rem .45rem;
                margin: .2rem 0 0 1.1rem; font-size: .72rem; min-width: 0; }
    .knxi-mfr { color: var(--bs-secondary-color); }
    .knxi-prod { font-weight: 600; min-width: 0; overflow-wrap: anywhere; }
    .knxi-type { color: var(--bs-secondary-color); font-size: .68rem; }
    .knxi-unknown { color: var(--bs-secondary-color); font-style: italic; }
    .knxi-more { margin-left: auto; padding: 0; border: 0; font-size: .66rem; line-height: 1.2; text-decoration: none; white-space: nowrap; }
    .knxi-c { font-size: .6rem; font-weight: 600; }
    .knxi-c-confirmed { background: var(--bs-success-bg-subtle); color: var(--bs-success-text-emphasis); }
    .knxi-c-likely    { background: var(--bs-info-bg-subtle); color: var(--bs-info-text-emphasis); }
    .knxi-c-unsure    { background: transparent; color: var(--bs-warning-text-emphasis); border: 1px dashed var(--bs-warning-border-subtle); }
    .knxi-c-silent    { background: transparent; color: var(--bs-secondary-color); border: 1px dashed var(--bs-border-color); }
    /* Badge: hover / focus (tap) opens a card with the sources as links, as wide as the identity line at most */
    .knxi-badge { cursor: help; border-radius: .3rem; }
    .knxi-badge:focus { outline: none; }
    .knxi-badge:focus-visible { outline: 2px solid var(--bs-primary); outline-offset: 1px; }
    .knxi-pop { position: absolute; left: 0; top: 100%; z-index: 5; margin-top: .15rem; max-width: min(30rem, 100%);
                display: flex; flex-direction: column; gap: .15rem; padding: .4rem .55rem; cursor: default;
                font-size: .68rem; line-height: 1.3; color: var(--bs-body-color); background: var(--bs-body-bg);
                border: 1px solid var(--bs-border-color); border-radius: .4rem; box-shadow: 0 .3rem .9rem rgba(0, 0, 0, .18);
                visibility: hidden; opacity: 0; transition: opacity .12s, visibility 0s .35s; }
    .knxi-badge:hover .knxi-pop, .knxi-badge:focus-within .knxi-pop { visibility: visible; opacity: 1; transition-delay: 0s; }
    .knxi-pop-head { font-weight: 600; color: var(--bs-secondary-color); }
    .knxi-src { overflow-wrap: anywhere; }
    .knxi-details { margin: .25rem 0 0 1.1rem; padding: .35rem .5rem; font-size: .68rem; background: var(--bs-tertiary-bg); border-radius: .35rem; }
    .knxi-details .mono { word-break: break-all; }
    /* No single long value (a source URL, an order info) may widen the grid beyond the card */
    .knxi-details .knxm-kv { grid-template-columns: max-content minmax(0, 1fr); }
    .knxi-details .knxm-kv > span { min-width: 0; overflow-wrap: anywhere; }
    .knxi-warn { overflow-wrap: anywhere; }
    /* Inventory */
    .knxi-toggle { display: inline-flex; align-items: baseline; gap: .2rem; padding: 0; border: 0; background: transparent; color: inherit; }
    .knxi-counts { display: inline-flex; flex-wrap: wrap; align-items: center; gap: .25rem .4rem; font-size: .7rem;
                   color: var(--bs-secondary-color); font-variant-numeric: tabular-nums; }
    .knxi-tbl { font-size: .7rem; }
    .knxi-tbl .knxi-line > td { background: var(--bs-tertiary-bg); padding-top: .3rem; padding-bottom: .3rem; }
    .knxi-tbl .knxi-silent > td { color: var(--bs-secondary-color); }
    .knxi-tbl .knxi-silent .knxi-bus { font-style: italic; }
    @media (min-width: 768px) { .knxi-prod-cell { min-width: 9rem; } }
  `, `
    /* ── Narrow screens (own sheet: style budget) ── */
    .knxm-lbl { display: none; }
    .knxm-btn-xs { font-size: .75rem; }
    .knxm-dl { font-size: .72rem; }
    .knxm-sort { display: none; }
    .knxm-order { font-size: .72rem; }
    .knxm-err-text, .knxm-srcs { font-size: .66rem; }
    /* A pane narrower than the 760 px tables (phones, and the overlay below ~950 px with its sidebar) stacks
       them: one block per row, no header (the group sort moves into the bar), column names inside the cells
       where a bare number would be unclear. No sideways scrolling. */
    .knxm-pane { container: knxm / inline-size; }
    @container knxm (max-width: 759.98px) {
      .knxm-sort { display: inline-flex; }
      .knxm-table, .knxt-gas, .knxt-sug { display: block; min-width: 0; table-layout: auto; --ga-w: 4.2rem; --hang: calc(var(--ga-w) + .6rem); }
      .knxm-table thead, .knxt-gas thead, .knxt-sug thead { display: none; }
      .knxm-table tbody, .knxt-gas tbody, .knxt-sug tbody { display: block; }
      .knxm-table tr, .knxt-gas tr, .knxt-sug tr { display: flex; flex-wrap: wrap; align-items: baseline; gap: .1rem .6rem;
                                                   border-bottom: 1px solid var(--bs-border-color-translucent); }
      .knxm-table td, .knxt-gas td, .knxt-sug td { display: block; padding: 0; border: 0; background: transparent; box-shadow: none; }
      .c-line, .c-name, .c-val, .c-data, .c-ev, .c-srcs { min-width: 0; }
      .c-name { max-width: none; }
      .knxm-table tr { padding: .45rem .75rem; font-size: .8125rem; }
      .knxt-gas tr, .knxt-sug tr { padding: .35rem 0; font-size: .75rem; }
      .knxt-gas tr:last-child, .knxt-sug tr:last-child { border-bottom: 0; }
      .knxm-table tr.table-active { background: var(--bs-secondary-bg); }
      .knxm-table tr.knxm-err { background: var(--bs-danger-bg-subtle); }
      .knxm-table tr.knxm-detail { background: var(--bs-tertiary-bg); padding-top: .35rem; }
      .knxm-detail > td { flex: 1 0 100%; font-size: .75rem; }
      .knxm-lbl { display: inline; margin-right: .25rem; color: var(--bs-secondary-color); font-family: var(--bs-body-font-family); }
      .knxm-sub, .knxm-srcs, .knxm-err-text, .knxm-apci, .knxm-dpt, .knxm-sugg-badge, .knxt-pwm, .knxt-st, .knxt-book, .knxt-differs { font-size: .6875rem; }
      /* Telegram: time · source · line / GW · service — target GA + name — value · data */
      .knxm-tg .c-time, .knxm-tg .c-src, .knxm-tg .c-line { font-size: .75rem; }
      .knxm-tg .c-time { order: 1; color: var(--bs-secondary-color); }
      .knxm-tg .c-time > .knxm-sub { display: inline; margin-right: .3rem; font-size: inherit; }
      .knxm-tg .c-src { order: 2; }
      .knxm-tg .c-line { order: 3; flex: 1 1 0; overflow: hidden; text-overflow: ellipsis; color: var(--bs-secondary-color); }
      .knxm-tg .c-line > .knxm-sub { display: inline; margin-left: .3rem; font-size: inherit; }
      .knxm-tg .c-apci { order: 4; margin-left: auto; }
      .knxm-tg .c-dst { order: 5; flex: 1 0 100%; }
      .knxm-tg .c-dst > .mono { font-weight: 600; margin-right: .4rem; }
      .knxm-tg .c-dst > .knxm-name { display: inline; }
      .knxm-tg .c-val { order: 6; flex: 1 1 auto; min-width: 0; font-weight: 600; }
      .knxm-tg .c-val > div { font-weight: 400; }
      .knxm-tg .c-data { order: 7; max-width: 40%; margin-left: auto; font-size: .6875rem; color: var(--bs-secondary-color); }
      /* Group address / device GA / suggestion: GA + name, then the facts; a hanging indent keeps every
         wrapped line under the name (the GA sits in the indent) */
      .knxm-grp tr { padding-left: calc(.75rem + var(--hang)); }
      .knxt-gas tr, .knxt-sug tr { padding-left: var(--hang); }
      .c-ga { order: 1; flex: 0 0 auto; min-width: var(--ga-w); margin-left: calc(-1 * var(--hang)); font-weight: 600; }
      .c-name { order: 2; flex: 1 1 0; }
      .knxm-grp tr::after, .knxt-gas tr::after, .knxt-sug tr::after { content: ''; order: 3; flex-basis: 100%; }
      .c-dpt { order: 4; } .c-last { order: 5; font-weight: 600; } .c-iv, .c-seen { order: 6; }
      .c-count { order: 7; text-align: left; }
      .c-srcs { order: 8; flex: 1 0 100%; overflow-wrap: anywhere; }
      .c-iv, .c-seen, .c-count { color: var(--bs-secondary-color); }
      .knxt-sug .c-st { order: 4; display: flex; align-items: center; gap: .4rem; }
      .knxt-sug .c-st > .knxt-conf { margin-top: 0 !important; }
      .knxt-sug .c-dpt { order: 5; }
      .knxt-sug .c-ev { order: 9; flex: 1 0 100%; }
    }
    /* Phones: 16 px fields (no iOS zoom), touch-sized controls, no text under 11 px */
    @media (max-width: 575.98px) {
      .knxm-tab-label { font-size: .6875rem; }
      .knxm-bar { gap: .5rem; }
      .knxm-bar .form-control, .knxm-bar .form-select, .knxm-sug-sel { font-size: 1rem; }
      .knxm-q, .knxt-q { flex: 1 1 0; min-width: 0; max-width: none; }
      .knxm-live-bar > .knxm-pause { order: 1; } .knxm-live-bar > .knxm-q { order: 2; flex-basis: 10rem; } .knxm-live-bar > .knxm-clear { order: 3; }
      .knxm-live-bar > .knxm-sel { order: 4; flex: 1 1 7.5rem; min-width: 0; max-width: none; }
      .knxm-live-bar > .knxm-checks { order: 5; margin-left: auto; gap: .75rem; }
      .knxm-live-bar > .knxm-stats { order: 6; flex: 1 0 100%; }
      .knxm-check { font-size: .8125rem; display: flex; align-items: center; min-height: var(--dash-touch-sm); }
      .knxm-check .form-check-input { margin-top: 0; }
      .knxm-check .form-check-label { padding-left: .1rem; }
      .knxm-stats { white-space: normal; font-size: .6875rem; }
      .knxm-small .badge { font-size: .6875rem; }
      .knxm-hint, .knxt-legend, .knxt-conf, .knxt-reasons, .knxi-type, .knxi-more, .knxi-c, .knxi-details, .knxi-pop { font-size: .6875rem; }
      .knxt-line-head { min-height: var(--dash-touch); align-items: center; font-size: .8125rem; }
      .knxi-toggle { min-height: var(--dash-touch-sm); align-items: center; }
      .knxi-more { padding: .4rem 0 .4rem .5rem; }
      .knxm-btn-xs, .knxm-order { display: inline-flex; align-items: center; justify-content: center;
                                  min-height: var(--dash-touch-sm); font-size: .8125rem; }
      .knxm-btn-xs { padding-left: .6rem !important; padding-right: .6rem !important; }
    }
  `],
})
export class AdminKnxComponent implements OnInit, OnDestroy {
  /** Auth headers of the admin session (X-Session-Token), supplied by the dashboard. */
  @Input() authHeaders: () => Record<string, string> = () => ({});
  /** A request came back 401: the dashboard drops the session and shows the password prompt. */
  @Output() unauthorized = new EventEmitter<void>();

  @ViewChild('liveScroll') liveScroll?: ElementRef<HTMLElement>;
  @ViewChild('topoSelCard') topoSelCard?: ElementRef<HTMLElement>;

  /** `short`: the label below 992 px — on phones under the icon (six segments at 375 px), up to 991 px next to it. */
  readonly tabs: { key: AdminKnxTab; label: string; short: string; icon: string }[] = [
    { key: 'live',     label: 'ADMIN_KNX_TAB_LIVE',     short: 'ADMIN_KNX_TAB_LIVE',         icon: '📡' },
    { key: 'topology', label: 'ADMIN_KNX_TAB_TOPO',     short: 'ADMIN_KNX_TAB_TOPO_SHORT',   icon: '🧭' },
    { key: 'valves',   label: 'ADMIN_KNX_TAB_VALVES',   short: 'ADMIN_KNX_TAB_VALVES_SHORT', icon: '🔧' },
    { key: 'groups',   label: 'ADMIN_KNX_TAB_GROUPS',   short: 'ADMIN_KNX_TAB_GROUPS_SHORT', icon: '🏷' },
    { key: 'history',  label: 'ADMIN_KNX_TAB_HISTORY',  short: 'ADMIN_KNX_TAB_HISTORY',      icon: '🕘' },
    { key: 'status',   label: 'ADMIN_KNX_TAB_STATUS',   short: 'ADMIN_KNX_TAB_STATUS',       icon: 'ℹ️' },
  ];
  tab: AdminKnxTab = 'live';

  /** Tooltip of a guessed DPT badge (kept as a field: no translate pipe per row). */
  guessedTitle = '';
  /** Badge text and tooltip of a learned name, per suggestion status (fields for the same reason). */
  sugBadge: Record<string, string> = { accepted: '', candidate: '', hint: '' };
  sugTitle = '';
  /** Column names shown inside the cells where a table stacks (below 768 px, no header); fields for the same reason. */
  lbl = { count: '', sources: '', interval: '' };
  /** Row whose detail line (raw frame, prio, hops …) is open. */
  expanded: number | null = null;
  apciOptions: string[] = [...BASE_APCIS];

  // ── Live ────────────────────────────────────────────────────────────────────
  private rows: TelegramRow[] = [];   // newest first, at most MAX_ROWS
  liveView: TelegramRow[] = [];
  liveCount  = 0;
  paused     = false;
  autoScroll = true;
  onlyGroups = false;
  liveQ      = '';
  liveApci   = '';
  liveError: string | null = null;
  /** The table has a hole (resume after a long pause, backlog > page): the missing ones are in Verlauf. */
  gap = false;
  private lastSeq    = 0;
  private initial    = true;
  private catchUpNext = true;
  /** The last page (a successful one) reported more pending: fetch the next one right away. */
  private liveFast   = false;

  // ── Status ──────────────────────────────────────────────────────────────────
  status: StatusView | null = null;
  statusError: string | null = null;
  downloading: string | null = null;
  downloadError: string | null = null;
  gaFile: File | null = null;
  gaUploading = false;
  gaResult: KnxGaUploadResult | null = null;
  gaError: string | null = null;

  // ── Gruppenadressen ─────────────────────────────────────────────────────────
  private groupRows: GroupRow[] = [];
  groupView: GroupRow[] = [];
  groupsCount = 0;
  groupQ = '';
  groupSort: GroupSort = 'ga';
  /** Sort buttons where the table header is hidden (stacked rows below 768 px). */
  readonly groupSorts: { key: GroupSort; label: string }[] = [
    { key: 'ga', label: 'ADMIN_KNX_COL_GA' }, { key: 'count', label: 'ADMIN_KNX_COL_COUNT' }, { key: 'last', label: 'ADMIN_KNX_COL_LAST_SEEN' },
  ];
  groupDesc = false;
  groupsLoaded = false;
  groupsError: string | null = null;

  // ── Verlauf ─────────────────────────────────────────────────────────────────
  histFrom  = '';
  histTo    = '';
  histQ     = '';
  histGa    = '';
  histSrc   = '';
  histApci  = '';
  histLimit = 1000;
  readonly histLimits = [500, 1000, 2000, 5000];
  histNewestFirst = true;
  private histRows: TelegramRow[] = [];   // ascending, as served
  histView: TelegramRow[] = [];
  histTruncated = false;
  /** Limit of the search whose results are shown (the select may have changed since). */
  histUsedLimit = 0;
  histFiles: string[] = [];
  histLoading = false;
  histSearched = false;
  histError: string | null = null;
  /** The user set the range (inputs or quick buttons); until then it is "the last hour" from now. */
  histRangeTouched = false;
  /** The range is a quick range relative to now ('1h' | '24h' | '7d'), null once edited by hand. */
  private histRel: string | null = '1h';
  /** A linked search waits until Verlauf is shown. */
  private histPending = false;
  /** The Verlauf search keys last applied from / written to the link (back / forward re-searches only when they differ). */
  private histLinkSig = '';
  private histReq = 0;

  // ── Geräte & Topologie ──────────────────────────────────────────────────────
  private topo: KnxTopology | null = null;
  topoLoaded = false;
  topoError: string | null = null;
  topoLines: TopoLine[] = [];
  topoSvg: TopoSvg | null = null;
  topoLegend: { role: RoleKey; label: string }[] = [];
  /** "Gelernt seit … · N Telegramme ausgewertet" and the replay line, formatted on arrival. */
  topoLearned = '';
  topoReplay = '';
  topoReplayRunning = false;
  topoArea = '';
  topoDeviceCount = 0;
  topoQ = '';
  /** Lines with the devices passing the filter (all when the filter is empty). */
  topoListView: { l: TopoLine; devices: TopoDevice[] }[] = [];
  topoShown = 0;
  /** IAs passing the filter (null: no filter) — the diagram dims the others. */
  topoMatch: Set<string> | null = null;
  collapsedLines: Record<string, boolean> = {};
  selectedIa: string | null = null;
  selected: TopoDevice | null = null;
  /** Picked in the device list: marked there and in the diagram, no detail card above the list. */
  selectedFromList = false;
  /** A linked selection is scrolled into view once it is rendered. */
  private topoReveal = false;
  private sugRows: SuggestionRow[] = [];
  sugView: SuggestionRow[] = [];
  sugLoaded = false;
  sugError: string | null = null;
  /** A status shows the open suggestions; 'book' the GAs the ETS GA book already names. */
  sugFilter: '' | SugStatus | 'book' = '';
  sugCounts: Record<SugStatus | 'book', number> = { accepted: 0, candidate: 0, hint: 0, book: 0 };
  sugTotal = 0;
  csvDownloading = false;
  csvError: string | null = null;
  /** Identity "Details" expanders that are open: 'l' + IA in the list, 'c' + IA in the card under the diagram. */
  identOpen: Record<string, boolean> = {};

  // ── Inventar (line scan, /api/admin/knx/inventory) ──────────────────────────
  private invDevices: unknown[] = [];
  private invMeta = new Map<string, KnxScanMeta>();
  private invFetchedAt = 0;
  invLoaded = false;
  invError: string | null = null;
  /** Where the backend looks for scan results (shown while there is none). */
  invDirs = '';
  /** The backend's problems with scan files (unreadable / too large files, skipped entries). */
  invWarnings: string[] = [];
  invWarningsMore = 0;
  invOpen = true;
  invLines: InvLine[] = [];
  invView: InvLine[] = [];
  invTotal = 0;
  invShown = 0;
  invCounts: Record<IdentConf | 'silent' | 'scanned', number> = { confirmed: 0, likely: 0, unsure: 0, silent: 0, scanned: 0 };
  readonly identConfs: IdentConf[] = ['confirmed', 'likely', 'unsure'];

  private uidSeq = 0;
  private pollGen = 0;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private destroyed = false;
  private subs: Subscription[] = [];
  private readonly onVisibility = () => {
    // Back from a hidden browser tab: catch up like after a pause
    if (document.visibilityState !== 'hidden') this.catchUpNext = true;
    this.syncPolling();
  };

  constructor(readonly translate: TranslateService, private readonly cdr: ChangeDetectorRef,
              private readonly link: DeepLinkService, private readonly host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    try {
      const t = localStorage.getItem(TAB_KEY) as AdminKnxTab | null;
      if (t && this.tabs.some(x => x.key === t)) this.tab = t;
      this.invOpen = localStorage.getItem(INV_OPEN_KEY) !== '0';
    } catch { /* storage blocked: default tab */ }
    this.setRange(3600_000, false);
    // The link wins over the stored tab; applied before the first poll so no other tab's poller starts
    this.applyLink(true);
    this.subs.push(this.link.external$.subscribe(() => {
      if (this.link.get('admin') !== 'knx') return;   // this section is closing: nothing to apply
      this.applyLink(false);
      this.cdr.markForCheck();
    }));
    this.subs.push(this.translate.stream(['ADMIN_KNX_COL_COUNT', 'ADMIN_KNX_COL_SOURCES', 'ADMIN_KNX_TOPO_INTERVAL'])
      .subscribe((t: Record<string, string>) => {
        this.lbl = { count: t['ADMIN_KNX_COL_COUNT'], sources: t['ADMIN_KNX_COL_SOURCES'], interval: t['ADMIN_KNX_TOPO_INTERVAL'] };
        this.cdr.markForCheck();
      }));
    this.subs.push(this.translate.stream('ADMIN_KNX_DPT_GUESSED').subscribe((s: string) => {
      this.guessedTitle = s;
      this.cdr.markForCheck();
    }));
    this.subs.push(this.translate.stream(['ADMIN_KNX_SUGGESTED', 'ADMIN_KNX_SUG_UNSURE', 'ADMIN_KNX_SUG_STATUS_HINT', 'ADMIN_KNX_SUGGESTED_HINT'])
      .subscribe((t: Record<string, string>) => {
        this.sugBadge = { accepted: t['ADMIN_KNX_SUGGESTED'], candidate: t['ADMIN_KNX_SUG_UNSURE'], hint: t['ADMIN_KNX_SUG_STATUS_HINT'] };
        this.sugTitle = t['ADMIN_KNX_SUGGESTED_HINT'];
        this.cdr.markForCheck();
      }));
    // Formatted dates / sizes follow the UI language
    this.subs.push(this.translate.onLangChange.subscribe(() => {
      this.rows.forEach(r => this.formatRowTime(r));
      this.histRows.forEach(r => this.formatRowTime(r));
      if (this.status) this.status = this.buildStatus(this.status.s);
      this.rebuildGroups();
      if (this.topo) this.buildTopology(this.topo);
      if (this.sugRows.length) this.buildSuggestions(this.sugRows.map(r => r.s));
      this.cdr.markForCheck();
    }));
    document.addEventListener('visibilitychange', this.onVisibility);
    this.syncPolling();
    if (this.tab === 'history') this.runLinkedSearch();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopTimers();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.subs.forEach(s => s.unsubscribe());
    // Overlay closed or another admin section shown: this section's keys go with it. (A lost session
    // keeps "admin=knx" and the keys, so the login brings the same view back.)
    if (this.link.get('admin') !== 'knx') Object.values(LINK).forEach(k => this.link.set(k, null));
  }

  setTab(t: AdminKnxTab): void {
    if (t === this.tab) return;
    this.tab = t;
    this.expanded = null;
    try { localStorage.setItem(TAB_KEY, t); } catch { /* ignore */ }
    this.link.set(LINK.tab, t);
    // Live polling stopped on the other tabs: catch up on what arrived meanwhile, like after a pause
    if (t === 'live') this.catchUpNext = true;
    if (t === 'history') this.refreshDefaultRange();
    this.syncPolling();
    if (t === 'history') this.runLinkedSearch();
  }

  // ── Deep link ───────────────────────────────────────────────────────────────

  /**
   * Applies the link (on creation, and after back / forward or an edited link): sub-tab, the filters,
   * the selected device and the Verlauf search. A missing key is the default, except the sub-tab
   * (missing: the shown / stored one stays, and is written). Read-only: at most a Verlauf GET.
   */
  private applyLink(initial: boolean): void {
    const l = this.link;
    const apci = (key: string): string => {
      const v = l.get(key) ?? '';
      if (!APCI_RE.test(v)) return '';
      this.noteApcis([v]);   // not seen on the bus yet: the select still shows it
      return v;
    };

    // Live
    this.liveQ = l.get(LINK.liveQ) ?? '';
    this.liveApci = apci(LINK.liveApci);
    this.onlyGroups = l.get(LINK.liveGroups) === '1';
    this.applyLiveFilter();

    // Gruppenadressen ("count", "-count": descending)
    this.groupQ = l.get(LINK.groupQ) ?? '';
    const sort = /^(-?)(ga|count|last)$/.exec(l.get(LINK.groupSort) ?? '');
    this.groupSort = sort ? sort[2] as GroupSort : 'ga';
    this.groupDesc = !!sort && sort[1] === '-';
    this.applyGroupView();

    // Geräte & Topologie
    this.topoQ = l.get(LINK.topoQ) ?? '';
    const sug = l.get(LINK.topoSug);
    this.sugFilter = SUG_FILTERS.find(s => s === sug) ?? '';
    const devLink = l.get(LINK.topoDev);
    const ia = devLink && IA_RE.test(devLink) ? devLink : null;
    const fromList = !!ia && l.get(LINK.topoList) === '1';
    if (ia !== this.selectedIa || fromList !== this.selectedFromList) {
      this.selectedIa = ia;
      this.selectedFromList = fromList;
      // Resolved by the next topology build when not loaded yet (and dropped there if unknown)
      this.selected = ia && this.topoLoaded ? this.findDevice(ia) : null;
      if (ia && this.topoLoaded && !this.selected) this.closeSelection();
      this.topoReveal = !!this.selectedIa;
    }
    this.applyTopoFilter();

    // Verlauf
    this.histNewestFirst = l.get(LINK.histOrder) !== 'asc';
    this.applyHistOrder();
    this.applyHistLink(initial);

    // Sub-tab: last, so a switch to Verlauf finds the linked search
    const t = l.get(LINK.tab);
    const tab = this.tabs.find(x => x.key === t)?.key;
    if (tab && tab !== this.tab) {
      if (initial) {
        this.tab = tab;
        try { localStorage.setItem(TAB_KEY, tab); } catch { /* ignore */ }
      } else {
        this.setTab(tab);
      }
    }
    l.set(LINK.tab, this.tab);
    if (!initial) {
      if (this.tab === 'history') this.runLinkedSearch();
      this.revealSelection();
    }
  }

  private histLinkSignature(): string {
    return HIST_SEARCH_KEYS.map(k => this.link.get(k) ?? '').join('\u0001');
  }

  /** The Verlauf form from the link; the search itself runs when Verlauf is shown ({@link runLinkedSearch}). */
  private applyHistLink(initial: boolean): void {
    const l = this.link;
    const sig = this.histLinkSignature();
    if (!initial && sig === this.histLinkSig) return;
    this.histLinkSig = sig;
    this.histQ = l.get(LINK.histQ) ?? '';
    this.histGa = l.get(LINK.histGa) ?? '';
    this.histSrc = l.get(LINK.histSrc) ?? '';
    const apci = l.get(LINK.histApci) ?? '';
    this.histApci = APCI_RE.test(apci) ? apci : '';
    if (this.histApci) this.noteApcis([this.histApci]);
    const limit = Number(l.get(LINK.histLimit));
    this.histLimit = this.histLimits.includes(limit) ? limit : HIST_LIMIT;
    const rel = l.get(LINK.histLast);
    const from = parseLinkTime(l.get(LINK.histFrom));
    const to = parseLinkTime(l.get(LINK.histTo));
    if (rel && HIST_RELS[rel]) {
      this.setRange(HIST_RELS[rel], false);
      this.histRangeTouched = true;
    } else if (from != null && to != null) {
      this.histFrom = toLocalInput(from);
      this.histTo = toLocalInput(to);
      this.histRel = null;
      this.histRangeTouched = true;
    } else {
      this.setRange(3600_000, false);
      this.histRangeTouched = false;
    }
    this.histPending = HIST_SEARCH_KEYS.some(k => l.get(k) != null);
    if (!this.histPending && !initial) {
      // No search in the link (any more): the untouched form, no results
      this.histReq++;
      this.histLoading = false;
      this.histSearched = false;
      this.histError = null;
      this.histRows = [];
      this.histTruncated = false;
      this.histFiles = [];
      this.applyHistOrder();
    }
  }

  /** A search named by the link: runs once, when Verlauf is shown (a read-only GET). */
  private runLinkedSearch(): void {
    if (!this.histPending) return;
    this.histPending = false;
    if (this.histRel) this.setRange(HIST_RELS[this.histRel], false);   // relative to when it runs
    this.searchHistory();
  }

  /** The Verlauf search just run, as link keys (relative quick ranges stay relative). */
  private writeHistLink(): void {
    const l = this.link;
    l.set(LINK.histLast, this.histRel);
    const stamp = (v: string) => { const t = parseLinkTime(v); return t == null ? null : linkStamp(t); };
    l.set(LINK.histFrom, this.histRel ? null : stamp(this.histFrom));
    l.set(LINK.histTo, this.histRel ? null : stamp(this.histTo));
    l.set(LINK.histQ, this.histQ.trim() || null);
    l.set(LINK.histGa, this.histGa.trim() || null);
    l.set(LINK.histSrc, this.histSrc.trim() || null);
    l.set(LINK.histApci, this.histApci || null);
    l.set(LINK.histLimit, this.histLimit === HIST_LIMIT ? null : this.histLimit);
    this.histLinkSig = this.histLinkSignature();
  }

  private writeTopoSelection(): void {
    this.link.set(LINK.topoDev, this.selectedIa);
    this.link.set(LINK.topoList, this.selectedIa && this.selectedFromList ? '1' : null);
  }

  /** Scrolls a linked selection into view once rendered: the card under the diagram, or the list entry. */
  private revealSelection(): void {
    if (!this.topoReveal || this.tab !== 'topology' || !this.selected || !this.topoSvg) return;
    this.topoReveal = false;
    this.cdr.detectChanges();
    const el = this.selectedFromList
      ? this.host.nativeElement.querySelector<HTMLElement>('.knxt-dev.sel')
      : this.topoSelCard?.nativeElement;
    el?.scrollIntoView({ block: 'nearest' });
  }

  /** Template handlers of the text filters and selects: filter, and mirror in the link. */
  liveFilterChanged(): void {
    this.applyLiveFilter();
    this.link.set(LINK.liveQ, this.liveQ.trim() || null);
    this.link.set(LINK.liveApci, this.liveApci || null);
    this.link.set(LINK.liveGroups, this.onlyGroups ? '1' : null);
  }

  groupFilterChanged(): void {
    this.applyGroupView();
    this.link.set(LINK.groupQ, this.groupQ.trim() || null);
  }

  topoFilterChanged(): void {
    this.applyTopoFilter();
    this.link.set(LINK.topoQ, this.topoQ.trim() || null);
  }

  sugFilterChanged(): void {
    this.applySugFilter();
    this.link.set(LINK.topoSug, this.sugFilter || null);
  }

  /** From / to edited by hand: an absolute range from now on. */
  histRangeEdited(): void {
    this.histRangeTouched = true;
    this.histRel = null;
  }

  trackRow   = (_: number, r: TelegramRow) => r.uid;
  trackGroup = (_: number, r: GroupRow) => r.g.dst;
  trackKey   = (_: number, r: { key: string }) => r.key;
  trackFile  = (_: number, r: FileRow) => r.date;
  trackTab   = (_: number, t: { key: AdminKnxTab }) => t.key;
  trackLine  = (_: number, x: { l: TopoLine }) => x.l.line;
  trackCol   = (_: number, c: SvgCol) => c.l.line;
  trackDev   = (_: number, d: { ia?: string; v?: TopoDevice }) => d.ia ?? d.v?.ia;
  trackTopoGa = (_: number, g: TopoGaRow) => g.g.ga;
  trackSug   = (_: number, r: SuggestionRow) => r.s.ga;
  trackRole  = (_: number, r: { role: RoleKey }) => r.role;
  trackInv   = (_: number, r: { ia?: string; line?: string }) => r.ia ?? r.line;
  trackSrc   = (i: number) => i;

  toggleRow(r: TelegramRow): void {
    this.expanded = this.expanded === r.uid ? null : r.uid;
  }

  // ── Polling ─────────────────────────────────────────────────────────────────

  /** (Re)starts exactly the pollers the visible tab needs; a new generation retires all older loops. */
  private syncPolling(): void {
    this.stopTimers();
    const gen = ++this.pollGen;
    if (this.destroyed || document.visibilityState === 'hidden') return;
    if (this.tab === 'live') {
      if (!this.paused) this.loop(gen, () => this.fetchLive(gen), () => this.liveFast ? 100 : LIVE_MS);
      this.loop(gen, () => this.fetchStatus(), () => STATUS_MS);
    } else if (this.tab === 'status') {
      this.loop(gen, () => this.fetchStatus(), () => STATUS_MS);
    } else if (this.tab === 'groups') {
      this.loop(gen, () => this.fetchGroups(), () => GROUPS_MS);
    } else if (this.tab === 'topology') {
      this.loop(gen, () => this.fetchTopology(), () => TOPO_MS);
    }
    // 'valves': <app-admin-knx-valves> polls on its own while it is shown.
  }

  /** Runs `run`, then schedules the next run after it settled (never two requests of one loop at a time). */
  private loop(gen: number, run: () => Promise<void>, delay: () => number): void {
    run().finally(() => {
      if (gen !== this.pollGen || this.destroyed) return;
      this.timers.push(setTimeout(() => this.loop(gen, run, delay), delay()));
    });
  }

  private stopTimers(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
  }

  /** GET/POST helper: 401 ends the admin session, anything else not OK becomes an error text. */
  private async request<T>(url: string, init: RequestInit = {}): Promise<{ data: T | null; error: string | null }> {
    try {
      const r = await fetch(url, { ...init, headers: { ...this.authHeaders(), ...((init.headers as Record<string, string> | undefined) ?? {}) } });
      if (r.status === 401) { this.unauthorized.emit(); return { data: null, error: 'HTTP 401' }; }
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
          const body = await r.text();
          try { const j = JSON.parse(body); if (j?.error) msg += ` – ${j.error}`; } catch { if (body && body.length < 200) msg += ` – ${body}`; }
        } catch { /* no body */ }
        return { data: null, error: msg };
      }
      return { data: await r.json() as T, error: null };
    } catch (e: any) {
      return { data: null, error: e?.message || String(e) };
    }
  }

  // ── Live ────────────────────────────────────────────────────────────────────

  togglePause(): void {
    this.paused = !this.paused;
    // Resume picks up at lastSeq; a backlog beyond CATCH_UP leaves a gap (flagged in the toolbar).
    if (!this.paused) this.catchUpNext = true;
    this.syncPolling();
  }

  clearLive(): void {
    this.rows = [];
    this.gap = false;
    this.expanded = null;
    this.applyLiveFilter();
  }

  private async fetchLive(gen: number): Promise<void> {
    const limit = this.catchUpNext ? CATCH_UP : LIVE_LIMIT;
    const { data, error } = await this.request<KnxLivePage>(`/api/admin/knx/live?after=${this.lastSeq}&limit=${limit}`);
    // Answer of a loop retired meanwhile (pause/resume, tab switch, hidden tab): it can be older than what
    // the new loop already applied, and its smaller latestSeq would look like a backend restart below.
    if (this.destroyed || gen !== this.pollGen) return;
    this.liveError = error;
    if (data) this.applyLive(data);
    else this.liveFast = false;   // failed: retry at the normal pace (still with the catch-up limit)
    this.cdr.markForCheck();
  }

  private applyLive(page: KnxLivePage): void {
    const latest = Number(page.latestSeq) || 0;
    this.catchUpNext = this.liveFast = false;
    if (latest < this.lastSeq) {
      // seq went backwards: a backend restart that counts from 0 again. (Ours seeds seq with its start
      // time, so it keeps growing across restarts and `after` simply continues.)
      this.lastSeq = 0;
      this.initial = true;
      this.catchUpNext = this.liveFast = true;
      return;
    }
    // Filter against the current lastSeq (not the one requested): an overlapping older request adds nothing twice.
    const fresh = (Array.isArray(page.telegrams) ? page.telegrams : [])
      .filter(t => t && typeof t.seq === 'number' && t.seq > this.lastSeq)
      .sort((a, b) => a.seq - b.seq);
    if (!fresh.length) {
      // Nothing newer buffered (seq may still jump, e.g. after a restart): continue from the server's latest
      this.lastSeq = Math.max(this.lastSeq, latest);
      this.initial = false;
      return;
    }
    const newest = fresh[fresh.length - 1].seq;
    if (latest - newest >= CATCH_UP) {
      // A server that pages oldest-first served an old page of a long backlog: jump to the newest CATCH_UP.
      if (!this.initial) this.gap = true;
      this.lastSeq = latest - CATCH_UP;
      this.catchUpNext = this.liveFast = true;
      return;
    }
    // Ours reports dropped older ones as `truncated`; without the flag, judge by the seq hole.
    const truncated = page.truncated ?? fresh[0].seq > this.lastSeq + 1;
    if (!this.initial && truncated) this.gap = true;
    const added: TelegramRow[] = [];
    for (let i = fresh.length - 1; i >= 0; i--) added.push(this.toRow(fresh[i]));
    this.rows = added.concat(this.rows);
    if (this.rows.length > MAX_ROWS) this.rows = this.rows.slice(0, MAX_ROWS);
    this.lastSeq = newest;
    this.noteApcis(fresh.map(t => t.apci));
    this.applyLiveFilter();
    if (this.autoScroll) {
      this.cdr.detectChanges();
      const el = this.liveScroll?.nativeElement;
      if (el) el.scrollTop = 0;
    }
    this.initial = false;
    // More pending than this page carried: fetch the rest right away
    this.catchUpNext = this.liveFast = newest < latest;
  }

  applyLiveFilter(): void {
    this.liveView = this.filterRows(this.rows, this.liveQ, this.liveApci, this.onlyGroups);
    this.liveCount = this.rows.length;
  }

  private filterRows(rows: TelegramRow[], q: string, apci: string, onlyGroups: boolean): TelegramRow[] {
    const needle = q.trim().toLowerCase();
    if (!needle && !apci && !onlyGroups) return rows;
    return rows.filter(r =>
      (!onlyGroups || r.isGroup) &&
      (!apci || r.t.apci === apci) &&
      (!needle || r.hay.includes(needle)));
  }

  private noteApcis(list: (string | undefined)[]): void {
    let added = false;
    for (const a of list) {
      if (a && !this.apciOptions.includes(a)) { this.apciOptions.push(a); added = true; }
    }
    if (added) this.apciOptions = [...BASE_APCIS, ...this.apciOptions.filter(a => !BASE_APCIS.includes(a)).sort()];
  }

  private toRow(t: KnxTelegram): TelegramRow {
    const apci = t.apci ?? '';
    const src = t.src ?? '';
    const r: TelegramRow = {
      uid: ++this.uidSeq,
      t,
      time: '',
      day: '',
      line: src.split('.').slice(0, 2).join('.'),
      isGroup: t.dstType === 'group',
      apciLabel: apciLabel(t),
      apciCls: apciClass(t),
      data: spacedHex(t.data),
      dptLabel: t.dpt ? (t.dptGuessed ? '~' + t.dpt : t.dpt) : '',
      sug: !t.name && t.suggestedName ? sugStatus(t.suggestionStatus) : '',
      hay: [t.dst, src, t.name, t.suggestedName, t.group, t.text, t.gw, apci].filter(Boolean).join('\u0001').toLowerCase(),
    };
    this.formatRowTime(r);
    return r;
  }

  private formatRowTime(r: TelegramRow): void {
    const d = new Date(r.t.ts);
    r.time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
    r.day = d.toLocaleDateString(this.locale, { weekday: 'short', day: '2-digit', month: '2-digit' });
  }

  spacedHex(hex: string | undefined): string { return spacedHex(hex); }

  valueJson(v: unknown): string { return v === undefined ? '' : JSON.stringify(v); }

  // ── Status ──────────────────────────────────────────────────────────────────

  refreshStatus(): void { this.fetchStatus(); }

  private async fetchStatus(): Promise<void> {
    const { data, error } = await this.request<KnxStatus>('/api/admin/knx/status');
    if (this.destroyed) return;
    this.statusError = error;
    if (data) {
      this.status = this.buildStatus(data);
      this.noteApcis(Object.keys(data.perApci ?? {}));
    }
    this.cdr.markForCheck();
  }

  private buildStatus(s: KnxStatus): StatusView {
    const counts = (m: Record<string, number> | undefined): CountRow[] => {
      const list = Object.entries(m ?? {}).map(([key, v]) => ({ key, count: Number(v) || 0, pct: 0 }));
      const max = list.reduce((a, r) => Math.max(a, r.count), 0);
      list.forEach(r => r.pct = max ? Math.round(r.count * 100 / max) : 0);
      return list;
    };
    const gateways = counts(s.perGateway).sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));
    const apcis = counts(s.perApci).sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
    const files = (s.files ?? []).slice().sort((a, b) => b.date.localeCompare(a.date))
      .map(f => ({ date: f.date, size: this.fmtBytes(f.bytes) }));
    return {
      s, gateways, apcis, files,
      started: s.startedAt ? this.fmtDateTime(s.startedAt) : '–',
      gaLoaded: s.gaBook?.loadedAt ? this.fmtDateTime(s.gaBook.loadedAt) : '–',
      gaWarnings: s.gaBook?.warnings ?? [],
      lastTelegramAgo:  s.lastTelegramAt ? this.ago(s.lastTelegramAt) : '',
      lastTelegramFull: s.lastTelegramAt ? this.fmtDateTime(s.lastTelegramAt) : '',
      lastErrorAt:      s.lastErrorAt ? this.fmtDateTime(s.lastErrorAt) : '',
    };
  }

  fmtNum(n: number | undefined | null): string {
    return n == null ? '–' : n.toLocaleString(this.locale, { maximumFractionDigits: 1 });
  }

  private fmtBytes(n: number): string {
    const fmt = (v: number) => v.toLocaleString(this.locale, { maximumFractionDigits: 1 });
    if (!(n >= 0)) return '–';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${fmt(n / 1024)} KB`;
    return `${fmt(n / 1024 / 1024)} MB`;
  }

  private fmtDateTime(ms: number): string {
    return new Date(ms).toLocaleString(this.locale, { dateStyle: 'short', timeStyle: 'medium' });
  }

  private get locale(): string {
    return uiLocale();
  }

  /** Day file via fetch (needs the auth header) → Blob → temporary object URL. */
  async download(date: string): Promise<void> {
    if (this.downloading) return;
    this.downloading = date;
    this.downloadError = null;
    const err = await this.saveFile(`/api/admin/knx/download?date=${encodeURIComponent(date)}`, `knx-bus-${date}.ndjson`);
    if (err) this.downloadError = `${date}: ${err}`;
    this.downloading = null;
    this.cdr.markForCheck();
  }

  /** GET with the auth header and hand the body to the browser as a file. Returns an error text, or null
   *  (also after a 401: the session ends and the password prompt shows instead). */
  private async saveFile(url: string, filename: string): Promise<string | null> {
    try {
      const r = await fetch(url, { headers: this.authHeaders() });
      if (r.status === 401) { this.unauthorized.emit(); return null; }
      if (!r.ok) return `HTTP ${r.status}`;
      const href = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoking right away can cancel the download in some browsers
      setTimeout(() => URL.revokeObjectURL(href), 10000);
      return null;
    } catch (e: any) {
      return e?.message || String(e);
    }
  }

  onGaFile(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    this.gaFile = input.files?.[0] ?? null;
    this.gaResult = null;
    this.gaError = null;
  }

  /** Sends the CSV bytes unchanged: the backend detects UTF-8 vs. Latin-1 (ETS exports either). */
  async uploadGa(input: HTMLInputElement): Promise<void> {
    const f = this.gaFile;
    if (!f || this.gaUploading) return;
    this.gaResult = null;
    this.gaError = null;
    if (f.size > GA_MAX_BYTES) {
      this.gaError = this.translate.instant('ADMIN_KNX_FILE_TOO_LARGE');
      return;
    }
    this.gaUploading = true;
    try {
      const body = await f.arrayBuffer();
      const { data, error } = await this.request<KnxGaUploadResult>('/api/admin/knx/ga', {
        method: 'POST', headers: { 'Content-Type': 'text/csv' }, body,
      });
      if (this.destroyed) return;
      this.gaError = error;
      if (data) {
        this.gaResult = data;
        this.gaFile = null;
        input.value = '';
        // New book size / warnings (names and DPTs show up in the next polls)
        this.fetchStatus();
      }
    } catch (e: any) {
      this.gaError = e?.message || String(e);
    } finally {
      this.gaUploading = false;
      this.cdr.markForCheck();
    }
  }

  // ── Gruppenadressen ─────────────────────────────────────────────────────────

  refreshGroups(): void { this.fetchGroups(); }

  private async fetchGroups(): Promise<void> {
    const { data, error } = await this.request<KnxGroupStat[]>('/api/admin/knx/groups');
    if (this.destroyed) return;
    this.groupsError = error;
    if (data) {
      this.groupRows = (Array.isArray(data) ? data : []).map(g => this.toGroupRow(g));
      this.groupsLoaded = true;
      this.applyGroupView();
    }
    this.cdr.markForCheck();
  }

  private rebuildGroups(): void {
    if (!this.groupRows.length) return;
    this.groupRows = this.groupRows.map(r => this.toGroupRow(r.g));
    this.applyGroupView();
  }

  private toGroupRow(g: KnxGroupStat): GroupRow {
    const lastText = g.lastText ?? (g.lastValue !== undefined ? String(g.lastValue) : '');
    const sources = (g.sources ?? []).join(', ');
    return {
      g,
      key: addrKey(g.dst ?? ''),
      dptLabel: g.dpt ? (g.dptGuessed ? '~' + g.dpt : g.dpt) : '',
      lastText,
      lastFull: g.lastSeen ? this.fmtDateTime(g.lastSeen) : '',
      ago: g.lastSeen ? this.ago(g.lastSeen) : '–',
      sources,
      sug: !g.name && g.suggestedName ? sugStatus(g.suggestionStatus) : '',
      hay: [g.dst, g.name, g.suggestedName, g.group, lastText, sources].filter(Boolean).join('\u0001').toLowerCase(),
    };
  }

  /** "12 s" / "5 min" / "3 h" / "2 d" (shown with the existing "vor …" / "… ago" key). */
  private ago(ts: number): string {
    const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (s < 60)    return `${s} s`;
    if (s < 3600)  return `${Math.floor(s / 60)} min`;
    if (s < 86400) return `${Math.floor(s / 3600)} h`;
    return `${Math.floor(s / 86400)} d`;
  }

  sortGroups(by: GroupSort): void {
    if (this.groupSort === by) this.groupDesc = !this.groupDesc;
    else { this.groupSort = by; this.groupDesc = by !== 'ga'; }
    this.applyGroupView();
    // "-count" = descending; the default (GA ascending) is not written
    this.link.set(LINK.groupSort, by === 'ga' && !this.groupDesc ? null : (this.groupDesc ? '-' : '') + by);
  }

  sortMark(by: GroupSort): string {
    return this.groupSort !== by ? '' : this.groupDesc ? ' ▾' : ' ▴';
  }

  applyGroupView(): void {
    const needle = this.groupQ.trim().toLowerCase();
    const list = needle ? this.groupRows.filter(r => r.hay.includes(needle)) : this.groupRows.slice();
    const by = this.groupSort;
    const dir = this.groupDesc ? -1 : 1;
    list.sort((a, b) => {
      const d = by === 'count' ? a.g.count - b.g.count
              : by === 'last'  ? (a.g.lastSeen ?? 0) - (b.g.lastSeen ?? 0)
              : 0;
      return d * dir || (a.key - b.key) * (by === 'ga' ? dir : 1);
    });
    this.groupView = list;
    this.groupsCount = this.groupRows.length;
  }

  // ── Verlauf ─────────────────────────────────────────────────────────────────

  setRange(ms: number, search = true): void {
    const now = Date.now();
    this.histFrom = toLocalInput(now - ms);
    // Round "to" up to the next minute so telegrams of the current minute are included
    this.histTo = toLocalInput(now + 60_000);
    this.histRel = Object.keys(HIST_RELS).find(k => HIST_RELS[k] === ms) ?? null;
    if (search) {
      this.histRangeTouched = true;   // a quick range is the user's choice
      this.searchHistory();
    }
  }

  /** Until the first search the default "last hour" counts from now, not from when the KNX section opened. */
  private refreshDefaultRange(): void {
    if (!this.histSearched && !this.histRangeTouched) this.setRange(3600_000, false);
  }

  async searchHistory(): Promise<void> {
    const req = ++this.histReq;   // a search still running is superseded: its answer is dropped below
    this.refreshDefaultRange();
    const from = new Date(this.histFrom).getTime();
    const to   = new Date(this.histTo).getTime();
    this.histError = null;
    if (!isFinite(from) || !isFinite(to) || from >= to) {
      this.histLoading = false;
      this.histError = this.translate.instant('ADMIN_KNX_RANGE_INVALID');
      return;
    }
    this.writeHistLink();
    const limit = this.histLimit;
    const p = new URLSearchParams({ from: String(from), to: String(to), limit: String(limit) });
    if (this.histQ.trim())   p.set('q', this.histQ.trim());
    if (this.histGa.trim())  p.set('ga', this.histGa.trim());
    if (this.histSrc.trim()) p.set('src', this.histSrc.trim());
    if (this.histApci)       p.set('apci', this.histApci);
    this.histLoading = true;
    this.expanded = null;
    const { data, error } = await this.request<KnxHistoryPage>(`/api/admin/knx/history?${p}`);
    if (this.destroyed || req !== this.histReq) return;
    this.histLoading = false;
    this.histError = error;
    if (data) {
      const list = (Array.isArray(data.telegrams) ? data.telegrams : []).slice().sort((a, b) => a.ts - b.ts);
      this.histRows = list.map(t => this.toRow(t));
      this.histTruncated = !!data.truncated;
      this.histUsedLimit = limit;
      this.histFiles = data.scannedFiles ?? [];
      this.histSearched = true;
      this.noteApcis(list.map(t => t.apci));
      this.applyHistOrder();
    }
    this.cdr.markForCheck();
  }

  toggleHistOrder(): void {
    this.histNewestFirst = !this.histNewestFirst;
    this.applyHistOrder();
    this.link.set(LINK.histOrder, this.histNewestFirst ? null : 'asc');
  }

  private applyHistOrder(): void {
    this.histView = this.histNewestFirst ? this.histRows.slice().reverse() : this.histRows;
  }

  // ── Geräte & Topologie ──────────────────────────────────────────────────────

  refreshTopology(): void { this.fetchTopology(true); }

  /**
   * Topology and suggestions together, plus the status for the listener address and multicast group in the
   * diagram, and (at most once a minute, or on ↻) the scan inventory.
   */
  private async fetchTopology(force = false): Promise<void> {
    const [topo, sug, inv] = await Promise.all([
      this.request<KnxTopology>('/api/admin/knx/topology'),
      this.request<KnxSuggestion[] | { suggestions?: KnxSuggestion[] }>('/api/admin/knx/suggestions'),
      this.fetchInventory(force),
      this.fetchStatus(),
    ]);
    if (this.destroyed) return;
    this.topoError = topo.error;
    this.sugError = sug.error;
    if (sug.data) {
      const d = sug.data;
      this.buildSuggestions(Array.isArray(d) ? d : Array.isArray(d.suggestions) ? d.suggestions : []);
      this.sugLoaded = true;
    }
    if (inv) this.applyInventory(inv);
    if (topo.data) {
      this.topo = topo.data;
      this.topoLoaded = true;
      this.buildTopology(topo.data);   // also rebuilds the inventory table (it shows which devices were heard)
    } else if (inv) {
      this.buildInventory();
      this.applyInvFilter();
    }
    this.cdr.markForCheck();
    this.revealSelection();
  }

  /** null: not due, or failed (the error is shown, the last good inventory stays; retried a minute later). */
  private async fetchInventory(force: boolean): Promise<KnxInventory | unknown[] | null> {
    if (!force && Date.now() - this.invFetchedAt < INV_MS) return null;
    this.invFetchedAt = Date.now();
    const { data, error } = await this.request<KnxInventory | unknown[]>('/api/admin/knx/inventory');
    // 404: a backend without the scan inventory — nothing to show, no error either
    this.invError = error && !error.startsWith('HTTP 404') ? error : null;
    return data;
  }

  /** Reads the inventory tolerantly: a bare device array, or { devices, lines | scans | files } (array or map by line). */
  private applyInventory(d: KnxInventory | unknown[]): void {
    const o = (Array.isArray(d) ? { devices: d } : d ?? {}) as KnxInventory;
    this.invDevices = Array.isArray(o.devices) ? o.devices : [];
    const meta = o.lines ?? o.scans ?? o.files;
    const list: KnxScanMeta[] = Array.isArray(meta) ? meta
      : meta && typeof meta === 'object' ? Object.entries(meta as Record<string, KnxScanMeta>).map(([line, m]) => ({ line, ...m }))
      : [];
    this.invMeta = new Map(list.filter(m => m && typeof m.line === 'string').map(m => [m.line as string, m]));
    this.invDirs = (Array.isArray(o.dirs) ? o.dirs : [])
      .map(x => typeof x === 'string' ? x : str((x as Record<string, unknown> | null)?.['path'])).filter(Boolean).join(', ');
    const warnings = (Array.isArray(o.warnings) ? o.warnings : []).map(str).filter(Boolean);
    this.invWarnings = warnings.slice(0, INV_WARN_MAX).map(w => clip(w, 300));
    this.invWarningsMore = warnings.length - this.invWarnings.length;
    this.invLoaded = true;
  }

  private buildTopology(t: KnxTopology): void {
    const lineMeta = new Map<string, KnxTopoLine>();
    for (const l of Array.isArray(t.lines) ? t.lines : []) if (l && typeof l.line === 'string') lineMeta.set(l.line, l);
    const lineOfIa = new Map<string, string>();
    lineMeta.forEach(l => (Array.isArray(l.devices) ? l.devices : []).forEach(ia => lineOfIa.set(ia, l.line)));
    // IP nodes by address; our own listener (if the backend lists it) becomes the diagram's listener box
    const ipLabel = new Map<string, string>();
    let selfIp = '';
    for (const n of Array.isArray(t.ipNodes) ? t.ipNodes : []) {
      if (!n || typeof n.ip !== 'string') continue;
      if (/listen|monitor|dashboard|self/i.test(n.kind ?? '') || n.ip === this.status?.s.localIp) selfIp = n.ip;
      else ipLabel.set(n.ip, n.label ?? '');
    }

    const byLine = new Map<string, TopoDevice[]>();
    let telegrams = 0;
    for (const d of Array.isArray(t.devices) ? t.devices : []) {
      if (!d || typeof d.ia !== 'string') continue;
      const line = d.line || lineOfIa.get(d.ia) || d.ia.split('.').slice(0, 2).join('.');
      if (!byLine.has(line)) byLine.set(line, []);
      byLine.get(line)!.push(this.toTopoDevice(d, line));
      telegrams += Number(d.count) || 0;
    }
    this.topoLines = [...new Set([...lineMeta.keys(), ...byLine.keys()])]
      .map(n => this.toTopoLine(n, lineMeta.get(n), (byLine.get(n) ?? []).sort((a, b) => a.key - b.key), ipLabel))
      .sort((a, b) => a.key - b.key);
    this.topoDeviceCount = this.topoLines.reduce((s, l) => s + l.devices.length, 0);
    for (const l of this.topoLines) {
      if (!(l.scannedAt || this.invMeta.has(l.line) || l.devices.some(v => v.ident))) continue;
      for (const v of l.devices) {
        if (v.ident) continue;
        v.notProbed = this.notProbedText(l.line, v.ia);
        v.notInScan = !v.notProbed;
      }
    }

    this.topoArea = (t.areas ?? (t.area != null ? [t.area] : [])).join(', ');
    this.topoLearned = this.translate.instant('ADMIN_KNX_TOPO_LEARNED', {
      since: t.learnedSince ? this.fmtDateTime(t.learnedSince) : '–',
      n: this.fmtNum(typeof t.telegrams === 'number' ? t.telegrams : telegrams),
    });
    const r = t.replay;
    this.topoReplayRunning = r?.done === false;
    this.topoReplay = r ? this.translate.instant(this.topoReplayRunning ? 'ADMIN_KNX_TOPO_REPLAY_RUNNING' : 'ADMIN_KNX_TOPO_REPLAY_DONE', {
      files: this.fmtNum(Array.isArray(r.files) ? r.files.length : Number(r.files) || 0),
      n: this.fmtNum(Number(r.telegrams) || 0),
    }) : '';

    const roles = new Set<RoleKey>();
    this.topoLines.forEach(l => l.devices.forEach(v => roles.add(v.role)));
    this.topoLegend = ROLE_ORDER.filter(r => roles.has(r))
      .map(role => ({ role, label: this.translate.instant('ADMIN_KNX_ROLE_' + role.toUpperCase()) as string }));

    this.selected = this.selectedIa ? this.findDevice(this.selectedIa) : null;
    if (!this.selected) this.closeSelection();
    this.topoSvg = this.layoutTopology(this.topoLines, ipLabel, selfIp);
    this.buildInventory();
    this.applyTopoFilter();
  }

  private toTopoDevice(d: KnxTopoDevice, line: string): TopoDevice {
    const role = roleKey(d.role);
    const group = this.translate.instant('ADMIN_KNX_ROLE_' + role.toUpperCase()) as string;
    // The backend's label is German and more specific ("Heizungsaktor (PWM), 3 Kanäle"); the English UI shows the group
    const label = this.translate.currentLang === 'de' && d.roleLabel ? d.roleLabel : group;
    const reasons = (Array.isArray(d.reasons) ? d.reasons : []).filter((x): x is string => typeof x === 'string' && !!x);
    const gas = (Array.isArray(d.gas) ? d.gas : [])
      .filter(g => g && typeof g.ga === 'string')
      .map(g => this.toTopoGa(g))
      .sort((a, b) => a.key - b.key);
    const silent = role === 'silent';
    const ident = this.toIdent(d.identity, d.ia);
    return {
      d, ia: d.ia, key: addrKey(d.ia), line, role,
      roleLabel: label,
      roleShort: group,
      roleTitle: d.roleLabel && d.roleLabel !== label ? d.roleLabel : '',
      conf: silent ? null : percent(d.confidence ?? undefined),
      reasons,
      count: silent && !Number(d.count) ? this.translate.instant('ADMIN_KNX_TOPO_SILENT_NOTE')
           : Number(d.count) === 1 ? this.translate.instant('ADMIN_KNX_TOPO_ONE_TELEGRAM')
           : this.translate.instant('ADMIN_KNX_TOPO_TELEGRAMS', { n: this.fmtNum(Number(d.count) || 0) }),
      ago: d.lastSeen ? this.ago(d.lastSeen) : '',
      lastFull: d.lastSeen ? this.fmtDateTime(d.lastSeen) : '',
      firstFull: d.firstSeen ? this.fmtDateTime(d.firstSeen) : '',
      gas, ident, silent, notInScan: false, notProbed: '',
      hay: [d.ia, d.role, d.roleLabel, group, ident?.hay, ...reasons, ...gas.flatMap(g => [g.g.ga, g.name, g.g.lastText])]
        .filter(Boolean).join('\u0001').toLowerCase(),
    };
  }

  /** Scan identity → view (labels in the UI language); null without one. */
  private toIdent(raw: unknown, ia?: string): IdentView | null {
    if (!raw || typeof raw !== 'object') return null;
    const i = raw as KnxIdentity;
    const tr = (k: string, p?: object) => this.translate.instant(k, p) as string;
    const mfrId = hexId(i.manufacturerId);
    const named = str(i.manufacturer);
    const hasName = !!named && !/^0x[0-9a-f]+$/i.test(named);
    const mfr = hasName ? named : mfrId ? tr('ADMIN_KNX_ID_MFR_ID', { id: mfrId }) : '–';
    const mfrShort = hasName ? named.split(/[\s(,]+/)[0] : mfrId;
    const prod = str(i.product);
    const known = !!prod && !/unbekannt|unknown/i.test(prod);
    const conf = identConf(i.confidence);
    const product = known ? prod : tr('ADMIN_KNX_ID_UNKNOWN_PRODUCT');
    const pre = mfrShort.toLowerCase() + ' ';
    const bare = known && mfrShort && product.toLowerCase().startsWith(pre) ? product.slice(pre.length) : product;
    const short = str(i.productShort) || (known ? productShort(prod, mfrShort, conf === 'unsure') : `${mfrShort} ?`.trim());
    const scannedMs = toMs(i.scannedAt);
    const scanned = scannedMs ? this.fmtDateTime(scannedMs) : '';
    const mask = str(i.mask).toUpperCase();
    const maskKnown = MASK_NAMES[mask];
    const maskName = maskKnown ? (maskKnown.startsWith('ADMIN_') ? tr(maskKnown) : maskKnown) : str(i.maskLabel);
    const basis = str(i.basis);
    const order = printable(i.orderText);
    const hw = str(i.hardwareType);
    const serial = fmtSerial(i.serial);
    const type = str(i.productType);
    const confLabel = tr('ADMIN_KNX_ID_CONF_' + conf.toUpperCase());
    const rows = [
      { k: tr('ADMIN_KNX_ID_MANUFACTURER'), v: hasName && mfrId ? `${mfr} (${mfrId})` : mfr, mono: false },
      { k: tr('ADMIN_KNX_ID_BASIS'),   v: basis, mono: false },
      { k: tr('ADMIN_KNX_ID_SERIAL'),  v: serial, mono: true },
      { k: tr('ADMIN_KNX_ID_MASK'),    v: mask && maskName ? `${mask} (${maskName})` : mask, mono: true },
      { k: tr('ADMIN_KNX_ID_HW'),      v: hw, mono: true },
      { k: tr('ADMIN_KNX_ID_FW'),      v: str(i.firmware) || str(i.firmwareRevision), mono: true },
      { k: tr('ADMIN_KNX_ID_PROGRAM'), v: str(i.programVersion), mono: true },
      { k: tr('ADMIN_KNX_ID_ORDER'),   v: [order, spacedHex(str(i.orderInfo))].filter(Boolean).join(' · '), mono: true },
      { k: tr('ADMIN_KNX_ID_SCAN'),    v: [scanned, str(i.scanFile)].filter(Boolean).join(' · '), mono: false },
    ].filter(r => r.v);
    return {
      i, mfr, mfrShort, mfrId, known, product, bare, short, type, conf, confLabel,
      sources: identSources(i.sources), basis, scanned, rows,
      hay: [ia ?? i.ia, mfr, mfrId, product, type, confLabel, serial, str(i.serial), order, hw].filter(Boolean).join('\u0001').toLowerCase(),
    };
  }

  toggleIdent(key: string): void {
    this.identOpen = { ...this.identOpen, [key]: !this.identOpen[key] };
  }

  private toTopoGa(g: KnxTopoGa): TopoGaRow {
    return {
      g,
      key: addrKey(g.ga),
      name: g.name || g.suggestedName || '',
      sug: !g.name && g.suggestedName ? sugStatus(g.suggestionStatus) : '',
      dptLabel: g.dpt ? (g.dptGuessed ? '~' + g.dpt : g.dpt) : '',
      interval: this.fmtInterval(g.intervalSec),
      count: this.fmtNum(Number(g.count) || 0),
    };
  }

  /** "~15 s" / "~5 min" / "~2 h"; "–" while no regular interval is known. */
  private fmtInterval(sec: number | undefined): string {
    if (typeof sec !== 'number' || !(sec > 0)) return '–';
    if (sec < 90)   return `~${Math.round(sec)} s`;
    if (sec < 5400) return `~${Math.round(sec / 60)} min`;
    return `~${this.fmtNum(sec / 3600)} h`;
  }

  private toTopoLine(line: string, meta: KnxTopoLine | undefined, devices: TopoDevice[], ipLabel: Map<string, string>): TopoLine {
    const backbone = line.split('.')[1] === '0';
    const gateways = (Array.isArray(meta?.gateways) ? meta!.gateways : []).filter((g): g is string => typeof g === 'string' && !!g);
    const router = meta?.router ?? '';
    const named = (ip: string) => ipLabel.get(ip) ? `${ip} (${ipLabel.get(ip)})` : ip;
    const gwText = backbone
      ? (gateways.length ? this.translate.instant('ADMIN_KNX_TOPO_VIA', { gw: gateways.map(named).join(', ') }) : '')
      : [gateways.length ? `${this.translate.instant('ADMIN_KNX_TOPO_IP_ROUTER')} ${gateways.join(', ')}` : '', router]
          .filter(Boolean).join(' · ');
    const silent = devices.filter(v => v.silent).length;
    // The line's scan date; a backend without the line field still dates it through the identities
    const identAt = Math.max(0, ...devices.map(v => toMs(v.ident?.i.scannedAt) ?? 0));
    const scannedAt = toMs(meta?.scannedAt) ?? toMs(this.invMeta.get(line)?.scannedAt) ?? (identAt || null);
    return {
      line, key: addrKey(line), backbone,
      title: this.translate.instant(backbone ? 'ADMIN_KNX_TOPO_MAIN_LINE' : 'ADMIN_KNX_TOPO_LINE', { line }),
      gateways, gwText, gwTitle: gateways.map(named).join(', '), router,
      rate: typeof meta?.rate === 'number' && isFinite(meta.rate) ? `${this.fmtNum(meta.rate)}/min` : '',
      devCount: devices.length === 1 ? this.translate.instant('ADMIN_KNX_TOPO_ONE_DEVICE')
                                     : this.translate.instant('ADMIN_KNX_TOPO_N_DEVICES', { n: devices.length }),
      silentText: silent ? this.translate.instant('ADMIN_KNX_TOPO_N_SILENT', { n: silent }) : '',
      scanned: scannedAt ? this.fmtDate(scannedAt) : '',
      scannedAt,
      devices,
    };
  }

  private fmtDate(ms: number): string {
    return new Date(ms).toLocaleDateString(this.locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  /**
   * Inline SVG model: IP nodes (our listener, HomeServer …) → KNXnet/IP routing band → one column per line
   * (coupler header, then the TP line with its devices). Natural size; the wrapper scrolls on phones.
   */
  private layoutTopology(lines: TopoLine[], ipLabel: Map<string, string>, selfIp: string): TopoSvg | null {
    if (!lines.length) return null;
    // Boxes grow a product line (from the line scan) under the address when any device has one
    const ids = lines.some(l => l.devices.some(v => v.ident));
    const P = 12, CW = 180, CG = 14, NW = 180, NH = 50, NG = 14, BH = 22, HH = 54, DI = 22, DH = ids ? 48 : 36, DS = DH + 8;
    const tr = (k: string, p?: object) => this.translate.instant(k, p) as string;
    const st = this.status?.s;

    // Top row: our listener, then IP nodes that are not the (primary) coupler of a TP line: HomeServer, other senders
    const routerIps = new Set(lines.filter(l => !l.backbone && l.gateways.length).map(l => l.gateways[0]));
    const top: Omit<SvgIpNode, keyof SvgBox>[] = [{
      title: tr('ADMIN_KNX_TOPO_LISTENER'), ip: st?.localIp || selfIp, listener: true,
      sub: tr('ADMIN_KNX_TOPO_LISTEN_ONLY') + (st?.iface ? ` · ${st.iface}` : ''),
      tip: tr('ADMIN_KNX_PASSIVE'),
    }];
    for (const ip of [...ipLabel.keys(), ...lines.filter(l => l.backbone).flatMap(l => l.gateways)]) {
      if (routerIps.has(ip) || top.some(n => n.ip === ip)) continue;
      const fed = lines.filter(l => l.gateways.includes(ip)).map(l => l.line);
      const label = ipLabel.get(ip) || '';
      top.push({
        title: label || ip, ip: label ? ip : '', listener: false,
        sub: fed.length ? tr('ADMIN_KNX_TOPO_LINE', { line: fed.join(', ') }) : '',
        tip: label ? `${label} · ${ip}` : ip,
      });
    }
    const colsW = lines.length * CW + (lines.length - 1) * CG;
    const topW = top.length * NW + (top.length - 1) * NG;
    const w = P * 2 + Math.max(colsW, topW);
    const bandY = P + NH + 18;
    const headY = bandY + BH + 18;
    const devY = headY + HH + 14;
    const maxDevs = lines.reduce((m, l) => Math.max(m, l.devices.length), 0);
    const h = (maxDevs ? devY + (maxDevs - 1) * DS + DH : headY + HH) + P;

    let ipPath = '';
    let tpPath = '';
    const nx = P + (w - 2 * P - topW) / 2;
    const nodes: SvgIpNode[] = top.map((n, i) => {
      const x = nx + i * (NW + NG);
      ipPath += `M${x + NW / 2} ${P + NH}V${bandY}`;
      return { ...n, x, y: P, w: NW, h: NH, title: clip(n.title, 27), sub: clip(n.sub, 30) };
    });
    const cx = P + (w - 2 * P - colsW) / 2;
    const cols: SvgCol[] = lines.map((l, i) => {
      const x = cx + i * (CW + CG);
      ipPath += `M${x + CW / 2} ${bandY + BH}V${headY}`;
      const busX = x + 9;
      const devs: SvgDev[] = l.devices.map((v, j) => {
        const y = devY + j * DS;
        tpPath += `M${busX} ${y + DH / 2}H${x + DI}`;
        const id = v.ident;
        return {
          v, x: x + DI, y, w: CW - DI, h: DH,
          prod: id ? clip(id.short, 26) : '',
          // A scan-only device is marked by its dashed box: its last line says what it is instead
          role: clip(v.silent && id?.type ? id.type : v.roleShort, 25),
          roleY: id ? 42 : 29,
          conf: v.conf !== null ? `${v.conf} %` : '',
          tip: [`${v.ia} · ${v.roleLabel}`,
                ...(id ? [`${id.mfr} · ${id.product} (${id.confLabel})`, id.type] : []),
                ...v.reasons].filter(Boolean).join('\n'),
        };
      });
      if (devs.length) tpPath += `M${busX} ${headY + HH}V${devs[devs.length - 1].y + DH / 2}`;
      const l2 = l.backbone
        ? (l.gateways.length ? tr('ADMIN_KNX_TOPO_VIA', { gw: l.gateways.join(', ') }) : tr('ADMIN_KNX_TOPO_IP_BACKBONE'))
        : l.gateways.length ? `${tr('ADMIN_KNX_TOPO_IP_ROUTER')} ${l.gateways[0]}${l.gateways.length > 1 ? ` +${l.gateways.length - 1}` : ''}`
        : tr('ADMIN_KNX_TOPO_NO_GW');
      const l3 = [l.backbone ? '' : l.router, l.devCount, l.rate].filter(Boolean).join(' · ');
      return {
        l, x, y: headY, w: CW, h: HH,
        l1: clip(l.title, 26), l2: clip(l2, 30), l3: clip(l3, 32),
        tip: [l.title, l.gwTitle, l.silentText, l.scanned ? tr('ADMIN_KNX_TOPO_SCANNED', { date: l.scanned }) : '']
          .filter(Boolean).join(' · '),
        devs,
      };
    });
    const band = [tr('ADMIN_KNX_TOPO_ROUTING'), `${st?.group || KNX_MCAST}:${st?.port || KNX_PORT}`,
                  this.topoArea ? tr('ADMIN_KNX_TOPO_AREA', { area: this.topoArea }) : ''].filter(Boolean).join(' · ');
    return {
      w: Math.ceil(w), h: Math.ceil(h), nodes, cols, ipPath, tpPath, ids,
      band: { x: P, y: bandY, w: w - 2 * P, h: BH, label: band },
    };
  }

  private findDevice(ia: string): TopoDevice | null {
    for (const l of this.topoLines) for (const v of l.devices) if (v.ia === ia) return v;
    return null;
  }

  /**
   * Diagram click: details right under the diagram (a second click on the same device closes
   * them). List click (fromList): marks the device in the list and in the diagram only — no
   * card is inserted above the list, so the list does not jump; a second click unmarks it.
   */
  selectDevice(ia: string, fromList = false): void {
    if (this.selectedIa === ia && this.selectedFromList === fromList) { this.closeSelection(); return; }
    this.selectedIa = ia;
    this.selected = this.findDevice(ia);
    this.selectedFromList = fromList;
    this.topoReveal = false;
    this.writeTopoSelection();
    if (fromList) return;
    this.cdr.detectChanges();
    this.topoSelCard?.nativeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /** Device list header (click / Enter / Space); the detail card under the diagram has no picker. */
  pickFromList(ia: string, detail: boolean, ev?: Event): void {
    if (detail) return;
    ev?.preventDefault();
    this.selectDevice(ia, true);
  }

  /** The detail card under the diagram (not for a device picked in the list). */
  get selectedCard(): TopoDevice | null {
    return this.selectedFromList ? null : this.selected;
  }

  closeSelection(): void {
    const had = this.selectedIa != null;
    this.selectedIa = null;
    this.selected = null;
    this.selectedFromList = false;
    this.topoReveal = false;
    // Also when a linked / selected device is not (any more) in the topology
    if (had) this.writeTopoSelection();
  }

  toggleLine(line: string): void {
    this.collapsedLines = { ...this.collapsedLines, [line]: !this.collapsedLines[line] };
  }

  /** One filter for the device list and the suggestions; the diagram dims what does not match. */
  applyTopoFilter(): void {
    const q = this.topoQ.trim().toLowerCase();
    this.topoListView = this.topoLines
      .map(l => ({ l, devices: q ? l.devices.filter(v => v.hay.includes(q)) : l.devices }))
      .filter(x => !q || x.devices.length);
    this.topoShown = this.topoListView.reduce((s, x) => s + x.devices.length, 0);
    this.topoMatch = q ? new Set(this.topoListView.flatMap(x => x.devices.map(v => v.ia))) : null;
    this.applySugFilter();
    this.applyInvFilter();
  }

  // ── Inventar ────────────────────────────────────────────────────────────────

  /**
   * One row per device known from the scan or the bus, grouped by line: the scan identity (from
   * /inventory, else the one on the topology device) and whether the device was heard on the bus.
   */
  private buildInventory(): void {
    const topo = new Map<string, TopoDevice>();
    this.topoLines.forEach(l => l.devices.forEach(v => topo.set(v.ia, v)));
    const idents = new Map<string, { ident: IdentView; line: string }>();
    for (const x of this.invDevices) {
      if (!x || typeof x !== 'object') continue;
      const o = x as KnxIdentity & { identity?: KnxIdentity };
      const raw = o.identity && typeof o.identity === 'object' ? { ...o.identity, ia: o.identity.ia ?? o.ia, line: o.identity.line ?? o.line } : o;
      const ia = str(raw.ia);
      const ident = ia ? this.toIdent(raw, ia) : null;
      if (ident) idents.set(ia, { ident, line: str(raw.line) || ia.split('.').slice(0, 2).join('.') });
    }
    topo.forEach(v => { if (v.ident && !idents.has(v.ia)) idents.set(v.ia, { ident: v.ident, line: v.line }); });
    const scannedLines = new Set<string>([...this.invMeta.keys(), ...[...idents.values()].map(x => x.line),
      ...this.topoLines.filter(l => l.scannedAt).map(l => l.line)]);
    if (!idents.size && !this.invMeta.size) {
      this.invLines = [];
      this.invTotal = 0;
      this.invCounts = { confirmed: 0, likely: 0, unsure: 0, silent: 0, scanned: 0 };
      return;
    }

    const tr = (k: string, p?: object) => this.translate.instant(k, p) as string;
    const byLine = new Map<string, InvRow[]>();
    const counts = { confirmed: 0, likely: 0, unsure: 0, silent: 0, scanned: 0 };
    for (const ia of new Set([...idents.keys(), ...topo.keys()])) {
      const id = idents.get(ia);
      const v = topo.get(ia);
      const line = id?.line ?? v!.line;
      const bus: InvBus = !this.topoLoaded ? 'pending' : v && !v.silent ? 'heard' : 'silent';
      if (id) { counts.scanned++; counts[id.ident.conf]++; if (bus === 'silent') counts.silent++; }
      const busLabel = bus === 'pending' ? '–' : tr('ADMIN_KNX_INV_' + bus.toUpperCase());
      const noIdent = id ? '' : !scannedLines.has(line) ? tr('ADMIN_KNX_ID_NOT_SCANNED')
                    : this.notProbedText(line, ia) || tr('ADMIN_KNX_ID_NOT_IN_SCAN');
      const row: InvRow = {
        ia, key: addrKey(ia), line, ident: id?.ident ?? null, noIdent, bus, busLabel,
        busTitle: bus === 'heard' ? v!.count + (v!.ago ? ` · ${tr('UPDATED_AGO', { time: v!.ago })}` : '')
                : bus === 'silent' ? tr('ADMIN_KNX_INV_SILENT_HINT') : '',
        telegrams: v && !v.silent ? Number(v.d.count) || 0 : 0,
        hay: [ia, id?.ident.hay, noIdent, busLabel, v?.roleLabel].filter(Boolean).join('\u0001').toLowerCase(),
      };
      if (!byLine.has(line)) byLine.set(line, []);
      byLine.get(line)!.push(row);
    }
    this.invCounts = counts;
    this.invTotal = [...byLine.values()].reduce((s, r) => s + r.length, 0);
    const lines = new Set([...byLine.keys(), ...this.invMeta.keys()]);
    this.invLines = [...lines].map(line => this.toInvLine(line, (byLine.get(line) ?? []).sort((a, b) => a.key - b.key)))
      .sort((a, b) => a.key - b.key);
  }

  /**
   * '' when the line scan probed `ia` (or its range is unknown); otherwise "not probed by the scan (probed
   * 1.1.0–1.1.239)": the scanner skips e.g. the routers' tunnelling addresses x.y.240–255, so a device heard
   * there was never asked and is not "missing" from the result.
   */
  private notProbedText(line: string, ia: string): string {
    const tr = (k: string, p?: object) => this.translate.instant(k, p) as string;
    const m = this.invMeta.get(line);
    if (!m) return '';
    // probed null + aborted: the scan stopped before it asked any address
    if (m.probed === null && m.aborted) return tr('ADMIN_KNX_ID_NOT_PROBED');
    const r = m.probed != null ? m.probed : m.requested;
    const b = rangeBounds(r);
    const n = Number(ia.split('.')[2]);
    if (!b || !Number.isInteger(n) || (n >= b[0] && n <= b[1])) return '';
    return `${tr('ADMIN_KNX_ID_NOT_PROBED')} (${tr('ADMIN_KNX_INV_PROBED', { range: rangeText(r) })})`;
  }

  private toInvLine(line: string, rows: InvRow[]): InvLine {
    const tr = (k: string, p?: object) => this.translate.instant(k, p) as string;
    const m = this.invMeta.get(line);
    const tl = this.topoLines.find(l => l.line === line);
    const at = toMs(m?.scannedAt) ?? tl?.scannedAt ?? null;
    const probed = rangeText(m?.probed);
    const file = str(m?.scanFile) || str(m?.file) || rows.map(r => str(r.ident?.i.scanFile)).find(Boolean) || '';
    const found = rows.filter(r => r.ident).length;
    const conOnly = iaList(m?.conOnly ?? m?.conOnlyNoReply);
    return {
      line, key: addrKey(line), rows,
      title: tr(line.split('.')[1] === '0' ? 'ADMIN_KNX_TOPO_MAIN_LINE' : 'ADMIN_KNX_TOPO_LINE', { line }),
      meta: [
        str(m?.router) ? `${tr('ADMIN_KNX_TOPO_IP_ROUTER')} ${str(m?.router)}` : '',
        at ? tr('ADMIN_KNX_TOPO_SCANNED', { date: this.fmtDateTime(at) }) : found ? '' : tr('ADMIN_KNX_ID_NOT_SCANNED'),
        probed ? tr('ADMIN_KNX_INV_PROBED', { range: probed }) : '',
        found ? tr('ADMIN_KNX_INV_FOUND', { n: found }) : '',
        file.split(/[\\/]/).pop(),
      ].filter(Boolean).join(' · '),
      file,
      warn: m?.aborted ? tr('ADMIN_KNX_INV_ABORTED') + (str(m.abortReason) ? `: ${str(m.abortReason)}` : '') : '',
      conOnly: conOnly.length ? `${tr('ADMIN_KNX_INV_CON_ONLY')}: ${conOnly.join(', ')}` : '',
    };
  }

  applyInvFilter(): void {
    const q = this.topoQ.trim().toLowerCase();
    this.invView = !q ? this.invLines : this.invLines
      .map(l => ({ ...l, rows: l.rows.filter(r => r.hay.includes(q)) }))
      .filter(l => l.rows.length);
    this.invShown = this.invView.reduce((s, l) => s + l.rows.length, 0);
  }

  toggleInventory(): void {
    this.invOpen = !this.invOpen;
    try { localStorage.setItem(INV_OPEN_KEY, this.invOpen ? '1' : '0'); } catch { /* ignore */ }
  }

  /** The whole inventory (not just the filtered rows) as a ";"-separated UTF-8 CSV, built in the browser. */
  downloadInventoryCsv(): void {
    const head = ['line', 'ia', 'manufacturer', 'manufacturerId', 'product', 'productType', 'confidence', 'bus', 'telegrams',
                  'serial', 'mask', 'hardwareType', 'firmware', 'programVersion', 'orderText', 'orderInfo', 'scannedAt', 'scanFile', 'sources'];
    const out = [head.map(csvField).join(';')];
    for (const l of this.invLines) for (const r of l.rows) {
      const id = r.ident;
      const i = id?.i;
      const at = toMs(i?.scannedAt);
      out.push([
        r.line, r.ia, id ? id.mfr : '', id?.mfrId ?? '', id?.known ? id.product : '', id?.type ?? '', id?.conf ?? '',
        r.bus === 'pending' ? '' : r.bus, r.bus === 'heard' ? r.telegrams : '',
        str(i?.serial), str(i?.mask), str(i?.hardwareType), str(i?.firmware) || str(i?.firmwareRevision), str(i?.programVersion),
        printable(i?.orderText), str(i?.orderInfo), at ? new Date(at).toISOString() : '', str(i?.scanFile),
        (id?.sources ?? []).map(s => [s.label, s.url].filter(Boolean).join(' ')).join(' | '),
      ].map(csvField).join(';'));
    }
    const d = new Date();
    const day = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const href = URL.createObjectURL(new Blob(['﻿' + out.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = href;
    a.download = `${this.translate.instant('ADMIN_KNX_INV_CSV_FILE')}-${day}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 10000);
  }

  private buildSuggestions(list: KnxSuggestion[]): void {
    // Open suggestions first — accepted (what the CSV adds), candidates, hints —, then the GAs the
    // book already names (the CSV keeps those names), accepted conflicts with the book first; by GA within each
    const rank = (r: SuggestionRow): number => !r.bookName ? { accepted: 0, candidate: 1, hint: 2 }[r.status]
      : r.differs && r.status === 'accepted' ? 3 : 4;
    this.sugRows = list.filter(s => s && typeof s.ga === 'string').map(s => this.toSugRow(s))
      .sort((a, b) => rank(a) - rank(b) || a.key - b.key);
    const c: Record<SugStatus | 'book', number> = { accepted: 0, candidate: 0, hint: 0, book: 0 };
    this.sugRows.forEach(r => c[r.bookName ? 'book' : r.status]++);
    this.sugCounts = c;
    this.sugTotal = this.sugRows.length;
    this.applySugFilter();
  }

  private toSugRow(s: KnxSuggestion): SuggestionRow {
    const m = s.matches ?? s.target?.matches;
    const n = s.total ?? s.target?.total;
    const evidence = (Array.isArray(s.evidence) ? s.evidence : s.evidence ? [s.evidence] : [])
      .filter(e => typeof e === 'string' && e).join(' · ');
    const candidates = (Array.isArray(s.candidates) ? s.candidates : []).filter(c => c?.name).map(c =>
      typeof c.matches === 'number' && typeof c.total === 'number' ? `${c.name} (${c.matches}/${c.total})`
      : typeof c.score === 'number' ? `${c.name} (${this.fmtNum(c.score)})`
      : c.name as string).join(', ');
    const suggested = typeof s.suggestedName === 'string' ? s.suggestedName.trim() : '';
    const bookName = typeof s.name === 'string' ? s.name.trim() : '';
    const name = bookName || suggested;
    const senders = (Array.isArray(s.senders) ? s.senders : []).join(', ');
    return {
      s, key: addrKey(s.ga), name, suggested, bookName,
      differs: !!bookName && !!suggested && bookName !== suggested,
      status: sugStatus(s.status),
      conf: percent(s.confidence),
      hits: typeof m === 'number' && typeof n === 'number' ? this.translate.instant('ADMIN_KNX_SUG_HITS', { m, n }) : '',
      evidence, candidates, senders,
      dptLabel: s.dpt ? (s.dptGuessed ? '~' + s.dpt : s.dpt) : '',
      hay: [s.ga, bookName, suggested, evidence, candidates, senders, s.target?.name].filter(Boolean).join('\u0001').toLowerCase(),
    };
  }

  applySugFilter(): void {
    const q = this.topoQ.trim().toLowerCase();
    const st = this.sugFilter;
    this.sugView = !q && !st ? this.sugRows : this.sugRows.filter(r =>
      (!st || (st === 'book' ? !!r.bookName : !r.bookName && r.status === st)) && (!q || r.hay.includes(q)));
  }

  /** ETS-importable CSV of the accepted suggestions and learned DPTs (reviewed by hand, then uploaded under Status). */
  async downloadSuggestionsCsv(): Promise<void> {
    if (this.csvDownloading) return;
    this.csvDownloading = true;
    this.csvError = null;
    const d = new Date();
    const day = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    this.csvError = await this.saveFile('/api/admin/knx/suggestions.csv', `${this.translate.instant('ADMIN_KNX_SUG_CSV_FILE')}-${day}.csv`);
    this.csvDownloading = false;
    this.cdr.markForCheck();
  }
}
